import React, { useEffect, useMemo, useState } from "react";
import {
  buildAgentHandoffSummary,
  buildZipImplementationPrompt,
} from "../../utils/devIdePromptGenerators";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  Stack,
  CircularProgress,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
} from "@mui/material";

const TASK_TYPES = ["ANALYSIS", "SCHEMA", "BACKEND", "FRONTEND", "TEST", "DOCS", "RELEASE", "GENERAL"];
const TASK_STATUSES = ["DRAFT", "PROPOSED", "APPROVED", "IN_PROGRESS", "IN_REVIEW", "DONE", "BLOCKED", "CANCELLED"];
const DEV_IDE_CHANGE_STATUSES = ["OPEN", "IN_PROGRESS", "DEVELOPMENT", "SANDBOX_TESTED", "INSTALLED_TO_TEST", "TESTED", "INSTALLED_TO_PROD", "DONE", "CANCELLED"];
const ALL_MODULES_VALUE = "__ALL_MODULES__";
const ALL_MODULES_LABEL = "All modules (project-wide)";

const isAllModulesValue = (value) => String(value || "").trim() === ALL_MODULES_VALUE;

const getModuleOptionValue = (row) => {
  if (row === null || row === undefined) return "";
  if (typeof row === "string") return row;
  if (typeof row === "object") return String(row.module_name || row.value || "");
  return String(row || "");
};

const getModuleOptionLabel = (row) => {
  if (row === null || row === undefined) return "";
  if (typeof row === "string") return isAllModulesValue(row) ? ALL_MODULES_LABEL : row;
  if (typeof row === "object") {
    const value = getModuleOptionValue(row);
    if (isAllModulesValue(value)) return String(row.label || ALL_MODULES_LABEL);
    return String(row.label || row.module_name || row.value || "");
  }
  return String(row || "");
};

const getQueueStatusChipProps = (status) => {
  const value = String(status || "").trim().toLowerCase();
  if (!value || value === "—") return { label: "—", color: "default", variant: "outlined" };
  if (["exported", "imported", "saved", "passed", "accepted", "success"].includes(value)) {
    return { label: status, color: "success", variant: "filled" };
  }
  if (["failed", "failure"].includes(value)) return { label: status, color: "error", variant: "filled" };
  if (["pending", "needs review", "running"].includes(value)) {
    return { label: status, color: "warning", variant: "outlined" };
  }
  if (value === "not started") return { label: status, color: "default", variant: "outlined" };
  return { label: status, color: "default", variant: "outlined" };
};


const QUEUE_WORKFLOW_STEPS = [
  ["packageExported", "Package exported"],
  ["packageImported", "Package imported"],
  ["changedFilesSaved", "Changed files saved"],
  ["frontendBuildCompleted", "Frontend build completed"],
  ["backendRestartCompleted", "Backend restart completed"],
  ["verificationSummaryCopied", "Human review / summary copied"],
];

const createDefaultQueueWorkflowStatus = () =>
  QUEUE_WORKFLOW_STEPS.reduce((acc, [key, label]) => {
    acc[key] = { label, state: "pending", timestamp: "", message: "" };
    return acc;
  }, {});

const mergeQueueWorkflowStatus = (incomingStatus) => {
  const base = createDefaultQueueWorkflowStatus();
  Object.entries(incomingStatus || {}).forEach(([key, value]) => {
    if (!base[key]) return;
    base[key] = {
      ...base[key],
      ...(value || {}),
      label: (value && value.label) || base[key].label,
      state: (value && value.state) || base[key].state,
      timestamp: (value && value.timestamp) || "",
      message: (value && value.message) || "",
    };
  });
  return base;
};

const getQueueWorkflowStateLabel = (state) => {
  if (state === "success") return "Success";
  if (state === "failure") return "Failed";
  if (state === "running") return "Running";
  return "Pending";
};

const getQueueStatusFromWorkflowStep = (status, pendingLabel = "Pending") => {
  const state = status && status.state ? status.state : "pending";
  if (state === "success") return "Success";
  if (state === "failure") return "Failed";
  if (state === "running") return "Running";
  return pendingLabel;
};

const normaliseQueueModuleName = (value) => {
  const moduleName = String(value || "").trim();
  if (!moduleName || isAllModulesValue(moduleName)) return "ALL";
  return moduleName;
};

const copyQueueTextToClipboard = async (text) => {
  if (!text) return false;
  if (navigator && navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
    await navigator.clipboard.writeText(text);
    return true;
  }
  return false;
};

const renderQueueStatusChip = (status, title) => {
  const props = getQueueStatusChipProps(status || "—");
  return (
    <Chip
      size="small"
      label={props.label}
      color={props.color}
      variant={props.variant}
      title={title || props.label}
      sx={{ maxWidth: "100%" }}
    />
  );
};
const FullChangePanel = ({ selectedProjectId, onSelectedProjectChange, onImportReturnedPackage } = {}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);

  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newProjectId, setNewProjectId] = useState("");
  const [newModuleName, setNewModuleName] = useState("");
  const [projectOptions, setProjectOptions] = useState([]);
  const [createModuleOptions, setCreateModuleOptions] = useState([]);
  const [selectedModuleOptions, setSelectedModuleOptions] = useState([]);
  const [projectOptionsLoading, setProjectOptionsLoading] = useState(false);
  const [projectOptionsError, setProjectOptionsError] = useState(null);
  const [moduleOptionsLoading, setModuleOptionsLoading] = useState(false);

  const [linkedLoading, setLinkedLoading] = useState(false);
  const [linkedError, setLinkedError] = useState(null);
  const [linkedChanges, setLinkedChanges] = useState([]);
  const [queueWorkflowStatusByChangeId, setQueueWorkflowStatusByChangeId] = useState({});
  const [queueWorkflowStatusError, setQueueWorkflowStatusError] = useState(null);
  const [queueActionMessage, setQueueActionMessage] = useState("");
  const [queueActionLoadingId, setQueueActionLoadingId] = useState(null);
  const [planningExportLoading, setPlanningExportLoading] = useState(false);
  const [planningImportLoading, setPlanningImportLoading] = useState(false);
  const [planningImportApplyLoading, setPlanningImportApplyLoading] = useState(false);
  const [planningImportDialogOpen, setPlanningImportDialogOpen] = useState(false);
  const [planningImportPreview, setPlanningImportPreview] = useState(null);
  const [planningImportManifest, setPlanningImportManifest] = useState(null);
  const [planningImportGptResponse, setPlanningImportGptResponse] = useState("");
  const [planningImportErrors, setPlanningImportErrors] = useState([]);
  const [planningImportWarnings, setPlanningImportWarnings] = useState([]);

  const [targetPreviewSourceProjectId, setTargetPreviewSourceProjectId] = useState("");
  const [targetPreviewTargetProjectId, setTargetPreviewTargetProjectId] = useState("");
  const [targetPreviewIncludeSql, setTargetPreviewIncludeSql] = useState(true);
  const [targetPreviewLoading, setTargetPreviewLoading] = useState(false);
  const [targetPreviewError, setTargetPreviewError] = useState(null);
  const [targetPreviewResult, setTargetPreviewResult] = useState(null);
  const [targetPreviewDialogOpen, setTargetPreviewDialogOpen] = useState(false);
  const [targetInstallLoading, setTargetInstallLoading] = useState(false);
  const [targetInstallError, setTargetInstallError] = useState(null);
  const [targetInstallResult, setTargetInstallResult] = useState(null);

  const [assignDevIdeChangeId, setAssignDevIdeChangeId] = useState("");
  const [assignOrder, setAssignOrder] = useState("");
  const [createLinkedTitle, setCreateLinkedTitle] = useState("");
  const [createLinkedDescription, setCreateLinkedDescription] = useState("");
  const [createLinkedStatus, setCreateLinkedStatus] = useState("OPEN");
  const [createLinkedOrder, setCreateLinkedOrder] = useState("");
  const [createLinkedScopeSummary, setCreateLinkedScopeSummary] = useState("");
  const [createLinkedAllowedFiles, setCreateLinkedAllowedFiles] = useState("");
  const [createLinkedImplementationNotes, setCreateLinkedImplementationNotes] = useState("");
  const [createLinkedLoading, setCreateLinkedLoading] = useState(false);

  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskCreateTitle, setTaskCreateTitle] = useState("");
  const [taskCreateType, setTaskCreateType] = useState("GENERAL");
  const [taskCreateLoading, setTaskCreateLoading] = useState(false);
  const [taskSaveLoading, setTaskSaveLoading] = useState(false);
  const [taskDeleteLoading, setTaskDeleteLoading] = useState(false);
  const [taskGenerateLoading, setTaskGenerateLoading] = useState(false);
  const [replaceTasksOnGenerate, setReplaceTasksOnGenerate] = useState(false);
  const [useLlmGeneration, setUseLlmGeneration] = useState(false);
  const [taskGenerationInfo, setTaskGenerationInfo] = useState(null);
  const [taskDiscoveryLoading, setTaskDiscoveryLoading] = useState(false);
  const [taskDiscoveryError, setTaskDiscoveryError] = useState(null);
  const [taskDiscoveryDialogOpen, setTaskDiscoveryDialogOpen] = useState(false);
  const [taskDiscoveryResult, setTaskDiscoveryResult] = useState(null);
  const [taskDiscoverySelectedFiles, setTaskDiscoverySelectedFiles] = useState([]);
  const [taskDiscoveryTargetIds, setTaskDiscoveryTargetIds] = useState([]);
  const [taskDiscoveryApplyLoading, setTaskDiscoveryApplyLoading] = useState(false);
  const [taskPatchPreviewLoading, setTaskPatchPreviewLoading] = useState(false);
  const [taskPatchPreviewError, setTaskPatchPreviewError] = useState(null);
  const [taskPatchDialogOpen, setTaskPatchDialogOpen] = useState(false);
  const [taskPatchPreviewResult, setTaskPatchPreviewResult] = useState(null);
  const [taskPatchApplyLoading, setTaskPatchApplyLoading] = useState(false);
  const [taskPatchApplyResult, setTaskPatchApplyResult] = useState(null);
  const [taskSqlProposalLoading, setTaskSqlProposalLoading] = useState(false);
  const [taskSqlProposalError, setTaskSqlProposalError] = useState(null);
  const [taskSchemaTargetLoading, setTaskSchemaTargetLoading] = useState(false);
  const [taskSchemaTargetError, setTaskSchemaTargetError] = useState(null);
  const [taskSchemaTargetResult, setTaskSchemaTargetResult] = useState(null);
  const [taskSqlDialogOpen, setTaskSqlDialogOpen] = useState(false);
  const [taskSqlProposalResult, setTaskSqlProposalResult] = useState(null);
  const [taskSqlSummaryDraft, setTaskSqlSummaryDraft] = useState("");
  const [taskSqlProposalDraft, setTaskSqlProposalDraft] = useState("");
  const [taskRollbackSqlDraft, setTaskRollbackSqlDraft] = useState("");
  const [taskSqlApplyLoading, setTaskSqlApplyLoading] = useState(false);
  const [taskSqlRunDialogOpen, setTaskSqlRunDialogOpen] = useState(false);
  const [taskSqlRunLoading, setTaskSqlRunLoading] = useState(false);
  const [taskSqlRunError, setTaskSqlRunError] = useState(null);
  const [taskSqlRunPreview, setTaskSqlRunPreview] = useState(null);
  const [taskSqlRunExecution, setTaskSqlRunExecution] = useState(null);

  const [plannerRunsLoading, setPlannerRunsLoading] = useState(false);
  const [plannerRunsError, setPlannerRunsError] = useState(null);
  const [plannerRuns, setPlannerRuns] = useState([]);

  const [plannerCreateLoading, setPlannerCreateLoading] = useState(false);
  const [plannerCreateError, setPlannerCreateError] = useState(null);
  const [plannerModule, setPlannerModule] = useState("devtools");
  const [plannerGoalText, setPlannerGoalText] = useState("");

  const loadProjectOptions = async () => {
    setProjectOptionsLoading(true);
    setProjectOptionsError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/projects");
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to load projects.");
      setProjectOptions(data.projects || []);
    } catch (e) {
      setProjectOptionsError(e.message);
      setProjectOptions([]);
    } finally {
      setProjectOptionsLoading(false);
    }
  };

  const loadModuleOptions = async (projectId, target = "selected") => {
    const project = String(projectId || "").trim();
    if (!project) {
      if (target === "create") setCreateModuleOptions([]);
      else setSelectedModuleOptions([]);
      return;
    }

    setModuleOptionsLoading(true);
    try {
      const res = await fetch(`/api/dev-ide/full-change/modules?project_id=${encodeURIComponent(project)}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to load modules.");
      const modules = data.modules || [];
      if (target === "create") setCreateModuleOptions(modules);
      else setSelectedModuleOptions(modules);
    } catch (e) {
      if (target === "create") setCreateModuleOptions([]);
      else setSelectedModuleOptions([]);
    } finally {
      setModuleOptionsLoading(false);
    }
  };

  const buildFullChangeContextText = (fc, linked, fallbackGoalText) => {
    const title = fc && fc.title ? String(fc.title) : "";
    const desc = fc && fc.description ? String(fc.description) : "";

    const lines = [];
    lines.push("FULL CHANGE CONTEXT");
    lines.push("");
    if (fc && fc.id !== undefined && fc.id !== null) lines.push(`Full Change ID: ${fc.id}`);
    if (title) lines.push(`Title: ${title}`);
    if (fc && fc.status) lines.push(`Status: ${fc.status}`);
    if (fc && fc.priority) lines.push(`Priority: ${fc.priority}`);
    if (fc && fc.owner) lines.push(`Owner: ${fc.owner}`);
    if (fc && fc.project_id) lines.push(`Project: ${fc.project_id}`);
    if (fc && fc.module_name) lines.push(`Module: ${getModuleOptionLabel(fc.module_name)}`);

    if (desc) {
      lines.push("");
      lines.push("Description:");
      lines.push(desc);
    }

    if (linked && Array.isArray(linked) && linked.length > 0) {
      lines.push("");
      lines.push("Linked Dev IDE Changes:");
      linked.forEach((c) => {
        const cid = c && c.id !== undefined && c.id !== null ? `#${c.id}` : "#?";
        const ctitle = c && c.title ? String(c.title) : "";
        const cstatus = c && c.status ? String(c.status) : "";
        const ord = c && c.full_change_order !== null && c.full_change_order !== undefined ? ` (order ${c.full_change_order})` : "";
        lines.push(`- ${cid}${ord} ${ctitle}${cstatus ? ` — ${cstatus}` : ""}`.trim());
      });
    }

    const goal = String(fallbackGoalText || "").trim();
    if (goal) {
      lines.push("");
      lines.push("Planner Goal Text:");
      lines.push(goal);
    }

    return lines.join("\n").trim();
  };

  const selectedTaskSummary = useMemo(() => {
    if (!selectedTask) return null;
    return `Task #${selectedTask.id || "new"} • ${selectedTask.task_type || "GENERAL"} • ${selectedTask.status || "DRAFT"}`;
  }, [selectedTask]);

  const selectedTaskAllowedFiles = useMemo(() => {
    return String((selectedTask && selectedTask.allowed_files) || "")
      .split("\n")
      .map((line) => String(line || "").trim())
      .filter(Boolean);
  }, [selectedTask]);

  const canRunTaskPatcher = useMemo(() => {
    const taskType = String((selectedTask && selectedTask.task_type) || "").toUpperCase();
    if (!selectedTask || !selectedTask.id) return false;
    if (!selected?.project_id) return false;
    if (!["FRONTEND", "BACKEND", "SCHEMA", "GENERAL"].includes(taskType)) return false;
    return selectedTaskAllowedFiles.length > 0;
  }, [selectedTask, selected, selectedTaskAllowedFiles]);

  const canResolveTaskSchemaTarget = useMemo(() => {
    const taskType = String((selectedTask && selectedTask.task_type) || "").toUpperCase();
    if (!selectedTask || !selectedTask.id) return false;
    if (!selected?.project_id) return false;
    return taskType === "SCHEMA";
  }, [selectedTask, selected]);

  const canRunTaskSqlProposal = useMemo(() => {
    const taskType = String((selectedTask && selectedTask.task_type) || "").toUpperCase();
    if (!selectedTask || !selectedTask.id) return false;
    if (!selected?.project_id) return false;
    if (taskType !== "SCHEMA") return false;
    return !!(taskSchemaTargetResult && taskSchemaTargetResult.generation_ready);
  }, [selectedTask, selected, taskSchemaTargetResult]);

  const canRunSavedTaskSql = useMemo(() => {
    const taskType = String((selectedTask && selectedTask.task_type) || "").toUpperCase();
    if (!selectedTask || !selectedTask.id) return false;
    if (!selected?.project_id) return false;
    if (taskType !== "SCHEMA") return false;
    return String((selectedTask && selectedTask.sql_proposal) || "").trim().length > 0;
  }, [selectedTask, selected]);

  const loadList = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/list");
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to load full changes.");
      setItems(data.full_changes || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadOne = async (id) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/dev-ide/full-change/get?id=${encodeURIComponent(id)}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to load full change.");
      setSelected(data.full_change || null);

      const fc = data.full_change || null;
      const seedTitle = fc && fc.title ? String(fc.title) : "";
      const seedDesc = fc && fc.description ? String(fc.description) : "";
      const seed = `${seedTitle}${seedDesc ? `\n\n${seedDesc}` : ""}`.trim();
      setPlannerGoalText((prev) => (prev && String(prev).trim().length > 0 ? prev : seed));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadLinked = async (fullChangeId) => {
    if (!fullChangeId) {
      setLinkedChanges([]);
      return;
    }
    setLinkedLoading(true);
    setLinkedError(null);
    try {
      const res = await fetch(`/api/dev-ide/full-change/linked-changes?full_change_id=${encodeURIComponent(fullChangeId)}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to load linked changes.");
      setLinkedChanges(data.linked_changes || []);
    } catch (e) {
      setLinkedError(e.message);
    } finally {
      setLinkedLoading(false);
    }
  };

  const getQueueProjectId = (linkedChange) => {
    return String(
      (linkedChange && (linkedChange.project_id || linkedChange.workflow_project_id)) ||
      (selected && selected.project_id) ||
      "default"
    ).trim() || "default";
  };

  const getNextLinkedChangeOrder = () => {
    const orders = (linkedChanges || [])
      .map((row) => Number(row && row.full_change_order))
      .filter((value) => Number.isFinite(value));
    if (!orders.length) return 1;
    return Math.max(...orders) + 1;
  };

  const pickDefaultTargetPreviewSourceProjectId = (projectList, fallbackProjectId = "") => {
    const list = projectList || [];
    const fallback = String(fallbackProjectId || "").trim();
    if (fallback) return fallback;
    const sandbox = list.find((p) => /sandbox/i.test(`${p?.project_id || ""} ${p?.label || ""}`));
    return sandbox?.project_id || list[0]?.project_id || "";
  };

  const pickDefaultTargetPreviewTargetProjectId = (projectList, sourceProjectId = "") => {
    const source = String(sourceProjectId || "").trim();
    const list = (projectList || []).filter((p) => p && p.project_id && p.project_id !== source);
    const testProject = list.find((p) => /test/i.test(`${p?.project_id || ""} ${p?.label || ""} ${p?.project_notes || ""}`));
    return testProject?.project_id || list[0]?.project_id || "";
  };

  const loadQueueWorkflowStatuses = async (changesToLoad = linkedChanges) => {
    const rows = Array.isArray(changesToLoad) ? changesToLoad : [];
    if (!rows.length) {
      setQueueWorkflowStatusByChangeId({});
      return;
    }

    setQueueWorkflowStatusError(null);
    const next = {};
    for (const change of rows) {
      if (!change || !change.id) continue;
      try {
        const params = new URLSearchParams();
        params.set("project_id", getQueueProjectId(change));
        params.set("change_id", change.id);
        const res = await fetch(`/api/dev-ide/workflow-status?${params.toString()}`);
        const data = await res.json();
        if (!data.success) throw new Error(data.error || "Failed to load workflow status.");
        next[change.id] = mergeQueueWorkflowStatus(data.workflow_status);
      } catch (e) {
        next[change.id] = createDefaultQueueWorkflowStatus();
        setQueueWorkflowStatusError(e.message);
      }
    }
    setQueueWorkflowStatusByChangeId(next);
  };

  const loadFullQueueDevIdeChange = async (linkedChange) => {
    if (!linkedChange || !linkedChange.id) return linkedChange;

    try {
      const params = new URLSearchParams();
      const projectId = getQueueProjectId(linkedChange);
      if (projectId) {
        params.set("project_id", projectId);
      }

      const query = params.toString();
      const res = await fetch(`/api/dev-ide/changes/${encodeURIComponent(linkedChange.id)}${query ? `?${query}` : ""}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to load linked DEV IDE change details.");

      return {
        ...linkedChange,
        ...(data.change || {}),
      };
    } catch (e) {
      setQueueWorkflowStatusError(e.message);
      return linkedChange;
    }
  };

  const markQueueWorkflowStep = async (linkedChange, stepKey, state = "success", message = "") => {
    if (!linkedChange || !linkedChange.id) return null;
    const timestamp = new Date().toLocaleString();
    const fallbackStatus = mergeQueueWorkflowStatus(queueWorkflowStatusByChangeId[linkedChange.id]);
    const stepLabel = (QUEUE_WORKFLOW_STEPS.find(([key]) => key === stepKey) || [null, stepKey])[1];

    setQueueWorkflowStatusByChangeId((prev) => ({
      ...(prev || {}),
      [linkedChange.id]: {
        ...fallbackStatus,
        [stepKey]: {
          ...(fallbackStatus[stepKey] || {}),
          label: (fallbackStatus[stepKey] && fallbackStatus[stepKey].label) || stepLabel,
          state,
          timestamp,
          message: message || "",
        },
      },
    }));

    try {
      const res = await fetch("/api/dev-ide/workflow-status/step", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_id: getQueueProjectId(linkedChange),
          change_id: linkedChange.id,
          step_key: stepKey,
          state,
          message: message || "",
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to save workflow status.");
      setQueueWorkflowStatusByChangeId((prev) => ({
        ...(prev || {}),
        [linkedChange.id]: mergeQueueWorkflowStatus(data.workflow_status),
      }));
      setQueueWorkflowStatusError(null);
      return data.workflow_status;
    } catch (e) {
      setQueueWorkflowStatusError(e.message);
      return null;
    }
  };

  const buildQueueExportAgentHandoffSummary = ({ linkedChange, moduleName }) => {
    const effectiveModule = moduleName && moduleName.trim().length > 0 ? moduleName : "ALL";
    const safeName = effectiveModule.toUpperCase() === "ALL" ? "all_modules" : effectiveModule.toLowerCase();
    const packageName = `${safeName}_dev_ide_files_with_docs.zip`;
    const workflowStatus = mergeQueueWorkflowStatus(queueWorkflowStatusByChangeId[linkedChange.id]);
    const workflowLines = QUEUE_WORKFLOW_STEPS.map(([key, label]) => {
      const status = workflowStatus?.[key] || {};
      const state = status.state || "pending";
      const message = status.message ? ` - ${status.message}` : "";
      return `${label}: ${state}${message}`;
    });

    return buildAgentHandoffSummary({
      phase: "Export",
      projectId: getQueueProjectId(linkedChange),
      codeModule: effectiveModule,
      includeDocs: true,
      docsMode: "all",
      docModules: ["ALL"],
      packageName,
      changedCodePaths: [],
      changedDocumentPaths: [],
      verificationLines: workflowLines,
      warnings: [],
      errors: [],
      recommendedNextStep: "Attach the exported ZIP and this handoff summary to the implementation agent, then import the returned package through DEV IDE.",
    });
  };

  const buildQueueResultSummary = (linkedChange) => {
    const status = mergeQueueWorkflowStatus(queueWorkflowStatusByChangeId[linkedChange.id]);
    return [
      "DEV IDE Agent Workflow Result Summary",
      "",
      `Full Change: ${selected && selected.id ? `#${selected.id} ${selected.title || ""}`.trim() : "None selected"}`,
      `Linked DEV IDE change: #${linkedChange.id} ${linkedChange.title || "Untitled change"}`,
      `Project: ${getQueueProjectId(linkedChange)}`,
      "",
      "Workflow status:",
      ...QUEUE_WORKFLOW_STEPS.map(([key, label], index) => {
        const row = status[key] || {};
        const timestamp = row.timestamp ? ` at ${row.timestamp}` : "";
        const message = row.message ? ` - ${row.message}` : "";
        return `${index + 1}. ${label}: ${getQueueWorkflowStateLabel(row.state)}${timestamp}${message}`;
      }),
      "",
      `Latest queue action: ${queueActionMessage || "No queue action message yet."}`,
    ].join("\n");
  };

  const handleQueueExportPackage = async (linkedChange) => {
    if (!linkedChange || !linkedChange.id) return;

    const initialModuleName = normaliseQueueModuleName(linkedChange.module || linkedChange.module_name || "ALL");
    const initialContextLabel = `linked DEV IDE change #${linkedChange.id} — ${linkedChange.title || "Untitled change"}`;

    setQueueActionLoadingId(`export-${linkedChange.id}`);
    setQueueActionMessage(`Preparing Development-tab export package for ${initialContextLabel}.`);
    await markQueueWorkflowStep(linkedChange, "packageExported", "running", `Export package started for ${initialContextLabel}.`);

    try {
      const exportChange = await loadFullQueueDevIdeChange(linkedChange);
      const moduleName = normaliseQueueModuleName(exportChange.module || exportChange.module_name || initialModuleName || "ALL");
      const projectId = getQueueProjectId(exportChange);
      const contextLabel = `linked DEV IDE change #${exportChange.id} — ${exportChange.title || "Untitled change"}`;

      const prompt = buildZipImplementationPrompt({
        projectId,
        codeModule: moduleName,
        includeDocs: true,
        docsMode: "all",
        docModules: ["ALL"],
        changeTitle: exportChange?.title || "",
        changeDescription: exportChange?.description || "",
        docsChanged: "Yes - update existing docs included in the ZIP when needed",
      });

      let copiedPrompt = false;
      try {
        copiedPrompt = await copyQueueTextToClipboard(prompt);
      } catch (err) {
        copiedPrompt = false;
      }

      const exportHandoffSummary = buildQueueExportAgentHandoffSummary({ linkedChange: exportChange, moduleName });
      let copiedHandoff = false;
      if (copiedPrompt) {
        try {
          copiedHandoff = await copyQueueTextToClipboard(`${prompt}\n\n${"=".repeat(80)}\n\n${exportHandoffSummary}`);
        } catch (err) {
          copiedHandoff = false;
        }
      }

      const successMessage = copiedHandoff
        ? `Agent export package started for module '${moduleName}' with all documents. Implementation prompt and agent handoff summary copied to clipboard.`
        : copiedPrompt
          ? `Agent export package started for module '${moduleName}' with all documents. Implementation prompt copied to clipboard.`
          : `Agent export package started for module '${moduleName}' with all documents. Clipboard copy was blocked by the browser.`;

      const params = new URLSearchParams();
      params.set("module", moduleName.toUpperCase() === "ALL" ? "ALL" : moduleName);
      if (projectId) {
        params.set("project_id", projectId);
      }
      params.set("include_documents", "true");

      const res = await fetch(`/api/dev-ide/zip-module?${params.toString()}`, { method: "GET" });
      if (!res.ok) {
        let errMsg = `Failed to download module ZIP (status ${res.status}).`;
        try {
          const data = await res.json();
          if (data && data.error) {
            errMsg = data.error;
          }
        } catch {}
        throw new Error(errMsg);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeName = moduleName.toUpperCase() === "ALL" ? "all_modules" : moduleName.toLowerCase();

      link.href = url;
      link.download = `${safeName}_dev_ide_files_with_docs.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setQueueActionMessage(successMessage);
      await markQueueWorkflowStep(exportChange, "packageExported", "success", successMessage);
    } catch (e) {
      const message = e.message || `Agent export package download failed for ${initialContextLabel}.`;
      setQueueActionMessage(message);
      await markQueueWorkflowStep(linkedChange, "packageExported", "failure", message);
    } finally {
      setQueueActionLoadingId(null);
    }
  };


  const handleExportPlanningPackage = async () => {
    if (!selectedId) return;
    setPlanningExportLoading(true);
    setQueueActionMessage("Preparing Full Change planning package for GPT.");
    try {
      const res = await fetch(`/api/dev-ide/full-change/export-planning-package?full_change_id=${encodeURIComponent(selectedId)}`, { method: "GET" });
      if (!res.ok) {
        let errMsg = `Failed to export planning package (status ${res.status}).`;
        try {
          const data = await res.json();
          if (data && data.error) errMsg = data.error;
          if (data && data.errors) errMsg = data.errors.join(", ");
        } catch {}
        throw new Error(errMsg);
      }

      const blob = await res.blob();
      const contentDisposition = res.headers.get("content-disposition") || "";
      const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const fallbackTitle = String((selected && selected.title) || `full_change_${selectedId}`).toLowerCase().replace(/[^a-z0-9_-]+/g, "_").replace(/^_+|_+$/g, "") || `full_change_${selectedId}`;
      const filename = filenameMatch && filenameMatch[1]
        ? decodeURIComponent(filenameMatch[1])
        : `${fallbackTitle}_planning_export.zip`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setQueueActionMessage("Full Change planning package exported. Preparing matching code/docs context ZIP.");

      try {
        const params = new URLSearchParams();
        params.set("module", "ALL");
        if (selected && selected.project_id) {
          params.set("project_id", selected.project_id);
        }
        params.set("include_documents", "true");

        const contextRes = await fetch(`/api/dev-ide/zip-module?${params.toString()}`, { method: "GET" });
        if (!contextRes.ok) {
          let errMsg = `Failed to download code/docs context ZIP (status ${contextRes.status}).`;
          try {
            const data = await contextRes.json();
            if (data && data.error) errMsg = data.error;
          } catch {}
          throw new Error(errMsg);
        }

        const contextBlob = await contextRes.blob();
        const contextUrl = window.URL.createObjectURL(contextBlob);
        const contextLink = document.createElement("a");
        contextLink.href = contextUrl;
        contextLink.download = "all_modules_dev_ide_files_with_docs.zip";
        document.body.appendChild(contextLink);
        contextLink.click();
        contextLink.remove();
        window.URL.revokeObjectURL(contextUrl);

        setQueueActionMessage("Full Change planning package and matching all-modules code/docs context ZIP exported for GPT.");
      } catch (contextError) {
        setQueueActionMessage((contextError && contextError.message)
          ? `Full Change planning package exported, but code/docs context ZIP failed: ${contextError.message}`
          : "Full Change planning package exported, but code/docs context ZIP failed.");
      }
    } catch (e) {
      setQueueActionMessage(e.message || "Full Change planning package export failed.");
    } finally {
      setPlanningExportLoading(false);
    }
  };

  const resetPlanningImportState = () => {
    setPlanningImportPreview(null);
    setPlanningImportManifest(null);
    setPlanningImportGptResponse("");
    setPlanningImportErrors([]);
    setPlanningImportWarnings([]);
  };

  const handlePlanningImportFileSelected = async (event) => {
    if (!selectedId) return;
    const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    event.target.value = "";
    if (!file) return;

    resetPlanningImportState();
    setPlanningImportLoading(true);
    setPlanningImportDialogOpen(true);
    setQueueActionMessage("Validating Full Change planning import package from GPT.");

    try {
      const formData = new FormData();
      formData.append("full_change_id", selectedId);
      formData.append("file", file);
      const res = await fetch("/api/dev-ide/full-change/import-planning-package/preview", {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (!data.success) {
        setPlanningImportErrors(data.errors || [data.error || "Planning import validation failed."]);
        setPlanningImportWarnings(data.warnings || []);
        setPlanningImportPreview(data.preview || null);
        setPlanningImportManifest(data.manifest || null);
        setPlanningImportGptResponse(data.gpt_response || "");
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Planning import validation failed.");
      }
      setPlanningImportPreview(data.preview || null);
      setPlanningImportManifest(data.manifest || null);
      setPlanningImportGptResponse(data.gpt_response || "");
      setPlanningImportWarnings(data.warnings || []);
      setPlanningImportErrors([]);
      setQueueActionMessage("Planning import preview loaded. Review before applying; no changes have been saved yet.");
    } catch (e) {
      setQueueActionMessage(e.message || "Planning import preview failed.");
    } finally {
      setPlanningImportLoading(false);
    }
  };

  const handleCancelPlanningImport = () => {
    resetPlanningImportState();
    setPlanningImportDialogOpen(false);
    setQueueActionMessage("Planning import cancelled. No planning changes were applied.");
  };

  const handleApplyPlanningImport = async () => {
    if (!selectedId || !planningImportManifest) return;
    setPlanningImportApplyLoading(true);
    setPlanningImportErrors([]);
    try {
      const res = await fetch("/api/dev-ide/full-change/import-planning-package/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_change_id: selectedId,
          manifest: planningImportManifest,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Planning import apply failed.");
      }
      setSelected(data.full_change || selected);
      setLinkedChanges(data.linked_changes || []);
      setTasks(data.tasks || []);
      await loadList();
      await loadLinked(selectedId);
      await loadTasks(selectedId);
      setPlanningImportWarnings(data.warnings || []);
      setQueueActionMessage("Planning import applied. Linked DEV IDE changes and tasks have been refreshed.");
      setPlanningImportDialogOpen(false);
      resetPlanningImportState();
    } catch (e) {
      setPlanningImportErrors([e.message || "Planning import apply failed."]);
      setQueueActionMessage(e.message || "Planning import apply failed.");
    } finally {
      setPlanningImportApplyLoading(false);
    }
  };

  const getPlanningActionChipColor = (action) => {
    if (action === "delete") return "error";
    if (action === "create") return "success";
    if (action === "update") return "warning";
    return "default";
  };

  const renderPlanningFieldChanges = (fieldChanges) => {
    if (!fieldChanges || Object.keys(fieldChanges).length === 0) {
      return <Typography variant="caption" color="text.secondary">No field changes proposed.</Typography>;
    }

    return (
      <Box sx={{ mt: 1, border: "1px solid rgba(0,0,0,0.08)", borderRadius: 1 }}>
        {Object.entries(fieldChanges).map(([field, change]) => (
          <Box key={field} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "160px 1fr 1fr" }, gap: 1, p: 0.75, borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>{field}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>From: {String(change.from ?? "")}</Typography>
            <Typography variant="caption" sx={{ whiteSpace: "pre-wrap" }}>To: {String(change.to ?? "")}</Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const getPlanningActionCounts = (rows = []) => {
    const counts = { create: 0, update: 0, "no-change": 0, delete: 0 };
    rows.forEach((row) => {
      const action = String(row?.action || "no-change").trim().toLowerCase() || "no-change";
      if (action === "no_change" || action === "no change") {
        counts["no-change"] += 1;
      } else if (Object.prototype.hasOwnProperty.call(counts, action)) {
        counts[action] += 1;
      }
    });
    return counts;
  };

  const renderPlanningActionCounts = (label, rows, summaryPrefix, previewSummary = {}) => {
    const rowCounts = getPlanningActionCounts(rows || []);
    const actions = ["create", "update", "no-change", "delete"];
    const getSummaryValue = (action) => {
      const normalizedAction = action.replace("-", "_");
      const pluralPrefix = summaryPrefix.endsWith("s") ? summaryPrefix : `${summaryPrefix}s`;
      const shortPrefix = summaryPrefix.replace("linked_dev_ide_change", "linked_change");
      const pluralShortPrefix = shortPrefix.endsWith("s") ? shortPrefix : `${shortPrefix}s`;
      const candidates = [
        `${summaryPrefix}_${action}`,
        `${summaryPrefix}_${normalizedAction}`,
        `${pluralPrefix}_${action}`,
        `${pluralPrefix}_${normalizedAction}`,
        `${shortPrefix}_${action}`,
        `${shortPrefix}_${normalizedAction}`,
        `${pluralShortPrefix}_${action}`,
        `${pluralShortPrefix}_${normalizedAction}`,
      ];
      for (const candidate of candidates) {
        if (previewSummary && previewSummary[candidate] !== undefined) {
          return previewSummary[candidate];
        }
      }
      return rowCounts[action];
    };

    return (
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
          {label}
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", rowGap: 0.75 }}>
          {actions.map((action) => (
            <Chip
              key={`${summaryPrefix}-${action}`}
              size="small"
              label={`${action}: ${getSummaryValue(action) ?? 0}`}
              color={getPlanningActionChipColor(action)}
              variant="outlined"
            />
          ))}
        </Stack>
      </Box>
    );
  };

  const getPlanningRowOrder = (row) => (
    row?.order ?? row?.queue_order ?? row?.display_order ?? row?.proposed_order ?? row?.sort_order ?? null
  );

  const renderPlanningPreviewSection = (title, rows, summaryPrefix, previewSummary = {}) => (
    <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.5 }, mb: 2, overflow: "hidden" }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
      {renderPlanningActionCounts("Action summary", rows, summaryPrefix, previewSummary)}
      {(!rows || rows.length === 0) ? (
        <Typography variant="caption" color="text.secondary">None proposed.</Typography>
      ) : (
        <Stack spacing={1}>
          {rows.map((row, index) => {
            const rowOrder = getPlanningRowOrder(row);
            return (
              <Paper key={`${title}-${index}`} variant="outlined" sx={{ p: { xs: 1, sm: 1.25 }, bgcolor: "background.default", overflow: "hidden" }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap sx={{ alignItems: { xs: "flex-start", sm: "center" }, flexWrap: "wrap", mb: 0.5 }}>
                  <Chip size="small" label={row.action || "no-change"} color={getPlanningActionChipColor(row.action)} />
                  {rowOrder !== null && rowOrder !== undefined && rowOrder !== "" ? (
                    <Chip size="small" label={`order: ${rowOrder}`} variant="outlined" />
                  ) : null}
                  <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>{row.title || row.task_title || "Untitled"}</Typography>
                  {row.existing_id ? <Typography variant="caption" color="text.secondary">existing #{row.existing_id}</Typography> : null}
                  {row.match_method ? <Typography variant="caption" color="text.secondary">match: {row.match_method}</Typography> : null}
                </Stack>
                {row.planning_key ? <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>planning_key: {row.planning_key}</Typography> : null}
                {(row.warnings || []).map((warning, idx) => (
                  <Alert key={idx} severity="warning" sx={{ mt: 0.75, py: 0.25 }}>{warning}</Alert>
                ))}
                {renderPlanningFieldChanges(row.field_changes)}
              </Paper>
            );
          })}
        </Stack>
      )}
    </Paper>
  );

  const renderPlanningImportDialog = () => {
    const previewSummary = (planningImportPreview && planningImportPreview.summary) || {};
    const fullChangePreview = (planningImportPreview && planningImportPreview.full_change) || { action: "no-change", field_changes: {}, warnings: [] };
    const manifestNotes = planningImportPreview && Array.isArray(planningImportPreview.notes) ? planningImportPreview.notes : [];
    const manifestWarnings = planningImportPreview && Array.isArray(planningImportPreview.manifest_warnings) ? planningImportPreview.manifest_warnings : [];
    const hasValidPlanningImportManifest = Boolean(planningImportManifest) && planningImportErrors.length === 0;

    return (
      <Dialog
        open={planningImportDialogOpen}
        onClose={() => {
          if (!planningImportLoading && !planningImportApplyLoading) handleCancelPlanningImport();
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 0, sm: 2 },
            width: { xs: "100%", sm: "calc(100% - 64px)" },
            height: { xs: "100%", sm: "auto" },
            maxHeight: { xs: "100%", sm: "calc(100% - 64px)" },
          },
        }}
      >
        <DialogTitle sx={{ pr: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>Full Change Planning Import Preview</DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 1.5, sm: 3 }, pb: { xs: 10, sm: 3 } }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Review this planning package before applying it. Apply creates or updates Full Change planning records, linked DEV IDE child changes, and governance tasks only; it does not save source-code or document file changes.
          </Alert>
          {planningImportLoading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Validating planning import package…</Typography>
            </Stack>
          )}

          {planningImportErrors.map((message, index) => (
            <Alert key={`planning-import-error-${index}`} severity="error" sx={{ mb: 1 }}>
              {message}
            </Alert>
          ))}

          {planningImportWarnings.map((message, index) => (
            <Alert key={`planning-import-warning-${index}`} severity="warning" sx={{ mb: 1 }}>
              {message}
            </Alert>
          ))}

          {manifestWarnings.map((message, index) => (
            <Alert key={`planning-manifest-warning-${index}`} severity="warning" sx={{ mb: 1 }}>
              {message}
            </Alert>
          ))}

          {!planningImportPreview && !planningImportLoading ? (
            <Typography variant="body2" color="text.secondary">
              No valid planning import preview is currently loaded.
            </Typography>
          ) : null}

          {planningImportPreview ? (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.5 }, overflow: "hidden" }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview summary</Typography>
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", rowGap: 0.75, mb: 1 }}>
                  {Object.entries(previewSummary).map(([key, value]) => (
                    <Chip key={key} size="small" label={`${key}: ${value ?? 0}`} variant="outlined" />
                  ))}
                  {Object.entries(previewSummary).length === 0 ? (
                    <Typography variant="caption" color="text.secondary">No backend summary counts were provided.</Typography>
                  ) : null}
                </Stack>
                {renderPlanningActionCounts("Linked DEV IDE changes", planningImportPreview.linked_dev_ide_changes, "linked_dev_ide_change", previewSummary)}
                {renderPlanningActionCounts("Tasks", planningImportPreview.tasks, "task", previewSummary)}
                {manifestNotes.length > 0 ? (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Planning notes</Typography>
                    {manifestNotes.map((note, index) => (
                      <Typography key={`planning-note-${index}`} variant="caption" sx={{ display: "block" }}>
                        • {String(note)}
                      </Typography>
                    ))}
                  </Box>
                ) : null}
              </Paper>

              <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.5 }, overflow: "hidden" }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Full Change field changes</Typography>
                <Paper variant="outlined" sx={{ p: 1, overflow: "hidden" }}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap sx={{ alignItems: { xs: "flex-start", sm: "center" }, flexWrap: "wrap", mb: 0.5 }}>
                    <Chip size="small" label={fullChangePreview.action || "no-change"} color={getPlanningActionChipColor(fullChangePreview.action)} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Parent Full Change</Typography>
                  </Stack>
                  {(fullChangePreview.warnings || []).map((warning, index) => (
                    <Alert key={`full-change-planning-warning-${index}`} severity="warning" sx={{ mt: 0.75, py: 0.25 }}>
                      {warning}
                    </Alert>
                  ))}
                  {renderPlanningFieldChanges(fullChangePreview.field_changes)}
                </Paper>
              </Paper>

              {renderPlanningPreviewSection("Linked DEV IDE change proposed actions", planningImportPreview.linked_dev_ide_changes, "linked_dev_ide_change", previewSummary)}
              {renderPlanningPreviewSection("Task proposed actions", planningImportPreview.tasks, "task", previewSummary)}

              {planningImportGptResponse ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>GPT_PLANNING_RESPONSE.md</Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      whiteSpace: "pre-wrap",
                      fontFamily: "monospace",
                      fontSize: 12,
                      maxHeight: { xs: 320, sm: 260 },
                      overflow: "auto",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {planningImportGptResponse}
                  </Paper>
                </Box>
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions
          sx={{
            position: { xs: "sticky", sm: "static" },
            bottom: 0,
            zIndex: 1,
            bgcolor: "background.paper",
            borderTop: { xs: "1px solid rgba(0,0,0,0.12)", sm: "none" },
            px: { xs: 1.5, sm: 3 },
            py: { xs: 1, sm: 1 },
            flexDirection: { xs: "column-reverse", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Button onClick={handleCancelPlanningImport} disabled={planningImportLoading || planningImportApplyLoading} sx={{ width: { xs: "100%", sm: "auto" } }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApplyPlanningImport}
            disabled={planningImportLoading || planningImportApplyLoading || !hasValidPlanningImportManifest}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {planningImportApplyLoading ? "Applying..." : "Apply Planning Import"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const handleRunTargetPreview = async () => {
    if (!selectedId) return;

    const sourceProjectId = String(targetPreviewSourceProjectId || "").trim();
    const targetProjectId = String(targetPreviewTargetProjectId || "").trim();
    if (!sourceProjectId) {
      setTargetPreviewError("Select a source project first.");
      setTargetPreviewDialogOpen(true);
      return;
    }
    if (!targetProjectId) {
      setTargetPreviewError("Select a target project first.");
      setTargetPreviewDialogOpen(true);
      return;
    }
    if (sourceProjectId === targetProjectId) {
      setTargetPreviewError("Source and target projects must be different.");
      setTargetPreviewDialogOpen(true);
      return;
    }

    setTargetPreviewDialogOpen(true);
    setTargetPreviewLoading(true);
    setTargetPreviewError(null);
    setTargetPreviewResult(null);
    setTargetInstallError(null);
    setTargetInstallResult(null);
    setQueueActionMessage(`Building aggregate target migration preview from ${sourceProjectId} to ${targetProjectId}.`);

    try {
      const res = await fetch("/api/dev-ide/full-change/target-preview-context", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_change_id: selectedId,
          source_project_id: sourceProjectId,
          target_project_id: targetProjectId,
          mode: "preview",
          include_sql: targetPreviewIncludeSql,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Full Change target preview failed.");
      }
      setTargetPreviewResult(data);
      const summary = data.summary || {};
      setQueueActionMessage(
        `Aggregate target preview ready for ${targetProjectId}. ${summary.changed_count || 0} changed candidate(s), ${summary.blocked_count || 0} blocked child change(s), ${summary.skipped_count || 0} skipped child change(s).`
      );
    } catch (e) {
      setTargetPreviewError(e.message || "Full Change target preview failed.");
      setQueueActionMessage(e.message || "Full Change target preview failed.");
    } finally {
      setTargetPreviewLoading(false);
    }
  };



  const handleInstallTargetMigration = async () => {
    if (!selectedId) return;

    const sourceProjectId = String(targetPreviewSourceProjectId || "").trim();
    const targetProjectId = String(targetPreviewTargetProjectId || "").trim();
    if (!sourceProjectId) {
      setTargetInstallError("Select a source project before installing.");
      setTargetPreviewDialogOpen(true);
      return;
    }
    if (!targetProjectId) {
      setTargetInstallError("Select a target project before installing.");
      setTargetPreviewDialogOpen(true);
      return;
    }
    if (sourceProjectId === targetProjectId) {
      setTargetInstallError("Source and target projects must be different before installing.");
      setTargetPreviewDialogOpen(true);
      return;
    }

    setTargetPreviewDialogOpen(true);
    setTargetInstallLoading(true);
    setTargetInstallError(null);
    setTargetInstallResult(null);
    setQueueActionMessage(`Installing eligible Full Change target migration candidates from ${sourceProjectId} to ${targetProjectId}.`);

    try {
      const res = await fetch("/api/dev-ide/full-change/target-install", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_change_id: selectedId,
          source_project_id: sourceProjectId,
          target_project_id: targetProjectId,
          include_sql: targetPreviewIncludeSql,
          change_summary: `Full Change ${selectedId} aggregate install to ${targetProjectId}`,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        if (data.path_conflicts || data.duplicate_changed_path_warnings || data.duplicate_path_warnings || data.preview) {
          setTargetInstallResult(data);
          if (data.preview) setTargetPreviewResult(data.preview);
        }
        throw new Error(data.error || data.block_reason || "Full Change target install failed.");
      }
      setTargetInstallResult(data);
      if (data.preview) setTargetPreviewResult(data.preview);
      const summary = data.summary || {};
      setQueueActionMessage(
        `Full Change target install finished for ${targetProjectId}. ${summary.applied_count || 0} file(s) applied, ${summary.skipped_count || 0} skipped, ${summary.error_count || 0} error(s), ${summary.warning_count || 0} warning(s).`
      );
    } catch (e) {
      setTargetInstallError(e.message || "Full Change target install failed.");
      setQueueActionMessage(e.message || "Full Change target install failed.");
    } finally {
      setTargetInstallLoading(false);
    }
  };


  const renderTargetPreviewSummaryChips = (summary = {}) => {
    const keys = [
      "linked_change_count",
      "total_count",
      "previewed_count",
      "skipped_count",
      "blocked_count",
      "candidate_count",
      "changed_count",
      "unchanged_count",
      "created_count",
      "updated_count",
      "error_count",
      "warning_count",
    ];
    return (
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", rowGap: 0.75 }}>
        {keys.map((key) => (
          <Chip key={key} size="small" label={`${key}: ${summary[key] ?? 0}`} variant="outlined" />
        ))}
      </Stack>
    );
  };



  const renderDuplicateTargetPathWarnings = (warnings = [], keyPrefix = "target-duplicate-warning") => {
    if (!warnings.length) return null;
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5, color: "warning.main" }}>
          Duplicate changed target path warnings
        </Typography>
        {warnings.map((warning, idx) => (
          <Alert key={`${keyPrefix}-${idx}`} severity="warning" sx={{ mb: 0.75, py: 0.25 }}>
            {warning.path}: linked changes {(warning.linked_changes || []).map((item) => `#${item.change_id}${item.order !== null && item.order !== undefined ? ` order ${item.order}` : ""}`).join(", ")}. Install will continue in Full Change order; later child changes may be skipped if the single-change apply recompute finds the file unchanged.
          </Alert>
        ))}
      </Box>
    );
  };


  const renderTargetInstallResult = () => {
    if (!targetInstallResult) return null;
    const groups = targetInstallResult.linked_changes || [];
    const duplicateWarnings = targetInstallResult.duplicate_changed_path_warnings || targetInstallResult.duplicate_path_warnings || targetInstallResult.path_conflicts || [];
    return (
      <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.5 }, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Install result</Typography>
        {renderTargetPreviewSummaryChips(targetInstallResult.summary || {})}
        {renderDuplicateTargetPathWarnings(duplicateWarnings, "target-install-duplicate-warning")}
        {groups.length > 0 ? (
          <Stack spacing={1} sx={{ mt: 1 }}>
            {groups.map((group, idx) => (
              <Paper key={`target-install-result-${group.change_id || idx}`} variant="outlined" sx={{ p: 1, bgcolor: "background.default" }}>
                <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap", mb: 0.75 }}>
                  <Chip size="small" label={`Change #${group.change_id || group.id || "?"}`} variant="outlined" />
                  <Chip size="small" label={`install: ${group.install_status || "—"}`} color={group.install_status === "applied" ? "success" : group.install_status === "error" || group.install_status === "blocked" ? "error" : "warning"} variant={group.install_status === "applied" ? "filled" : "outlined"} />
                  {group.order !== null && group.order !== undefined ? <Chip size="small" label={`order: ${group.order}`} variant="outlined" /> : null}
                </Stack>
                <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: "break-word", mb: 0.75 }}>{group.title || "Untitled linked change"}</Typography>
                {group.block_reason ? <Alert severity="error" sx={{ mb: 0.75, py: 0.25 }}>{group.block_reason}</Alert> : null}
                {group.skip_reason ? <Alert severity="warning" sx={{ mb: 0.75, py: 0.25 }}>{group.skip_reason}</Alert> : null}
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", mb: 0.75 }}>
                  <Chip size="small" label={`applied: ${(group.summary && group.summary.applied_count) || 0}`} variant="outlined" />
                  <Chip size="small" label={`skipped: ${(group.summary && group.summary.skipped_count) || 0}`} variant="outlined" />
                  <Chip size="small" label={`blocked: ${(group.summary && group.summary.blocked_count) || 0}`} variant="outlined" />
                  <Chip size="small" label={`errors: ${(group.summary && group.summary.error_count) || 0}`} variant="outlined" />
                </Stack>
                {(group.applied || []).map((row, rowIdx) => (
                  <Typography key={`target-install-applied-${group.change_id}-${rowIdx}`} variant="caption" sx={{ display: "block", fontFamily: "monospace", overflowWrap: "anywhere" }}>
                    applied: {row.path || "(no path)"}{row.backup_path ? ` · backup: ${row.backup_path}` : ""}
                  </Typography>
                ))}
                {(group.errors || []).map((row, rowIdx) => (
                  <Alert key={`target-install-error-${group.change_id}-${rowIdx}`} severity="error" sx={{ mt: 0.75, py: 0.25 }}>
                    {row.path ? `${row.path}: ` : ""}{row.error || row.message || JSON.stringify(row)}
                  </Alert>
                ))}
              </Paper>
            ))}
          </Stack>
        ) : null}
      </Paper>
    );
  };


  const renderTargetPreviewDialog = () => {
    const groups = (targetPreviewResult && targetPreviewResult.linked_changes) || [];
    const summary = (targetPreviewResult && targetPreviewResult.summary) || {};

    return (
      <Dialog
        open={targetPreviewDialogOpen}
        onClose={() => { if (!targetPreviewLoading) setTargetPreviewDialogOpen(false); }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 0, sm: 2 },
            width: { xs: "100%", sm: "calc(100% - 64px)" },
            height: { xs: "100%", sm: "auto" },
            maxHeight: { xs: "100%", sm: "calc(100% - 64px)" },
          },
        }}
      >
        <DialogTitle>Full Change Aggregate Target Preview</DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 1.5, sm: 3 }, pb: { xs: 10, sm: 3 } }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            This aggregate target migration review uses the same preview field concepts as the Changes tab migration preview. Duplicate changed target paths across child changes are shown as warnings; install still delegates each eligible child to the existing single-change apply service in Full Change order.
          </Alert>

          {targetPreviewLoading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Building aggregate target preview…</Typography>
            </Stack>
          )}

          {targetPreviewError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {targetPreviewError}
            </Alert>
          )}

          {targetInstallError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {targetInstallError}
            </Alert>
          )}

          {targetInstallLoading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Installing aggregate target candidates…</Typography>
            </Stack>
          )}

          {renderTargetInstallResult()}

          {!targetPreviewLoading && !targetPreviewError && !targetPreviewResult && (
            <Typography variant="body2" color="text.secondary">
              No aggregate target preview loaded yet.
            </Typography>
          )}

          {targetPreviewResult && (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.5 } }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview summary</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Source project: {targetPreviewResult.source_project_id || "—"} · Target project: {targetPreviewResult.target_project_id || "—"} · Include SQL: {targetPreviewResult.include_sql ? "yes" : "no"}
                </Typography>
                {renderTargetPreviewSummaryChips(summary)}
                {renderDuplicateTargetPathWarnings(targetPreviewResult.duplicate_changed_path_warnings || targetPreviewResult.duplicate_path_warnings || [], "target-preview-duplicate-warning")}
              </Paper>

              {groups.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No linked child change preview groups were returned.</Typography>
              ) : null}

              {groups.map((group, groupIndex) => {
                const candidates = group.candidates || [];
                const errors = group.errors || [];
                return (
                  <Paper key={group.change_id || groupIndex} variant="outlined" sx={{ p: { xs: 1, sm: 1.5 }, overflow: "hidden" }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap sx={{ alignItems: { xs: "flex-start", sm: "center" }, flexWrap: "wrap", mb: 1 }}>
                      <Chip size="small" label={`Change #${group.change_id || group.id || "?"}`} variant="outlined" />
                      <Chip size="small" label={`Status: ${group.preview_status || "previewed"}`} color={group.preview_status === "blocked" ? "error" : group.preview_status === "skipped" ? "warning" : "success"} variant={group.preview_status === "previewed" ? "filled" : "outlined"} />
                      {group.order !== null && group.order !== undefined ? <Chip size="small" label={`order: ${group.order}`} variant="outlined" /> : null}
                      <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: "break-word" }}>{group.title || "Untitled linked change"}</Typography>
                    </Stack>

                    {group.skip_reason ? <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>{group.skip_reason}</Alert> : null}
                    {group.block_reason ? <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>{group.block_reason}</Alert> : null}

                    <Box sx={{ mb: 1 }}>{renderTargetPreviewSummaryChips(group.summary || {})}</Box>

                    {errors.length > 0 ? (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="error" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>Errors</Typography>
                        {errors.map((err, idx) => (
                          <Alert key={`target-preview-error-${group.change_id || groupIndex}-${idx}`} severity="error" sx={{ mb: 0.75, py: 0.25 }}>
                            {err.path ? `${err.path}: ` : ""}{err.error || err.message || JSON.stringify(err)}
                          </Alert>
                        ))}
                      </Box>
                    ) : null}

                    {candidates.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">No candidate files returned for this linked child change.</Typography>
                    ) : (
                      <Stack spacing={1}>
                        {candidates.map((candidate, idx) => (
                          <Paper key={`${candidate.path || idx}-${idx}`} variant="outlined" sx={{ p: 1, bgcolor: "background.default", overflow: "hidden" }}>
                            <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap", mb: 0.75 }}>
                              <Chip size="small" label={`kind: ${candidate.kind || "—"}`} variant="outlined" />
                              <Chip size="small" label={`target_exists: ${candidate.target_exists ? "true" : "false"}`} variant="outlined" />
                              <Chip size="small" label={`changed: ${candidate.changed ? "true" : "false"}`} color={candidate.changed ? "warning" : "default"} variant="outlined" />
                              <Chip size="small" label={`source_exists: ${candidate.source_exists ? "true" : "false"}`} variant="outlined" />
                            </Stack>
                            <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 700, overflowWrap: "anywhere", mb: 0.75 }}>
                              {candidate.path || "(no path)"}
                            </Typography>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1,
                                whiteSpace: "pre-wrap",
                                fontFamily: "monospace",
                                fontSize: 12,
                                maxHeight: { xs: 260, sm: 360 },
                                overflow: "auto",
                                bgcolor: "background.paper",
                              }}
                            >
                              {candidate.diff_text || "(no diff text)"}
                            </Paper>
                          </Paper>
                        ))}
                      </Stack>
                    )}
                  </Paper>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            position: { xs: "sticky", sm: "static" },
            bottom: 0,
            zIndex: 1,
            bgcolor: "background.paper",
            borderTop: { xs: "1px solid rgba(0,0,0,0.12)", sm: "none" },
            px: { xs: 1.5, sm: 3 },
            py: { xs: 1, sm: 1 },
            flexDirection: { xs: "column-reverse", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Button onClick={() => setTargetPreviewDialogOpen(false)} disabled={targetPreviewLoading || targetInstallLoading} sx={{ width: { xs: "100%", sm: "auto" } }}>Close</Button>
          <Button onClick={handleRunTargetPreview} variant="outlined" disabled={targetPreviewLoading || targetInstallLoading || !selectedId} sx={{ width: { xs: "100%", sm: "auto" } }}>
            {targetPreviewLoading ? "Previewing…" : "Refresh Preview"}
          </Button>
          <Button onClick={handleInstallTargetMigration} variant="contained" color="warning" disabled={targetPreviewLoading || targetInstallLoading || !selectedId || !targetPreviewSourceProjectId || !targetPreviewTargetProjectId || targetPreviewSourceProjectId === targetPreviewTargetProjectId} sx={{ width: { xs: "100%", sm: "auto" } }}>
            {targetInstallLoading ? "Installing…" : "Install to Target"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const handleQueueImportReturnedPackage = async (linkedChange) => {
    if (!linkedChange || !linkedChange.id) return;

    const initialContextLabel = `linked DEV IDE change #${linkedChange.id} — ${linkedChange.title || "Untitled change"}`;
    setQueueActionLoadingId(`import-${linkedChange.id}`);
    setQueueActionMessage(`Preparing Development-tab import review for ${initialContextLabel}.`);

    try {
      const importChange = await loadFullQueueDevIdeChange(linkedChange);
      const projectId = getQueueProjectId(importChange) || selectedProjectId || "";
      const contextLabel = `linked DEV IDE change #${importChange.id} — ${importChange.title || "Untitled change"}`;

      if (projectId && typeof onSelectedProjectChange === "function") {
        onSelectedProjectChange(projectId);
      }

      if (typeof onImportReturnedPackage !== "function") {
        throw new Error("Development import workflow is not available from this tab.");
      }

      onImportReturnedPackage({
        changeId: importChange.id,
        projectId,
        includeDocuments: true,
        source: "full-change-implementation-queue",
        fullChangeId: selected && selected.id ? selected.id : null,
      });

      const message = `Development tab opened for ${contextLabel}. The existing Import ZIP with docs workflow is ready to load the returned package.`;
      setQueueActionMessage(message);
    } catch (e) {
      setQueueActionMessage(e.message || `Import returned package failed to start for ${initialContextLabel}.`);
    } finally {
      setQueueActionLoadingId(null);
    }
  };

  const handleQueueCopyResultSummary = async (linkedChange) => {
    if (!linkedChange || !linkedChange.id) return;
    setQueueActionLoadingId(`summary-${linkedChange.id}`);
    try {
      const summary = buildQueueResultSummary(linkedChange);
      const copied = await copyQueueTextToClipboard(summary);
      if (!copied) throw new Error("Clipboard copy was blocked by the browser.");
      const message = `Result summary copied for linked DEV IDE change #${linkedChange.id}.`;
      setQueueActionMessage(message);
      await markQueueWorkflowStep(linkedChange, "verificationSummaryCopied", "success", message);
    } catch (e) {
      const message = e.message || `Copy result summary failed for linked DEV IDE change #${linkedChange.id}.`;
      setQueueActionMessage(message);
      await markQueueWorkflowStep(linkedChange, "verificationSummaryCopied", "failure", message);
    } finally {
      setQueueActionLoadingId(null);
    }
  };

  const loadTasks = async (fullChangeId) => {
    if (!fullChangeId) {
      setTasks([]);
      setSelectedTaskId(null);
      setSelectedTask(null);
      return;
    }
    setTasksLoading(true);
    setTasksError(null);
    try {
      const res = await fetch(`/api/dev-ide/full-change/tasks?full_change_id=${encodeURIComponent(fullChangeId)}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to load tasks.");
      const taskRows = data.tasks || [];
      setTasks(taskRows);
      setSelectedTask((prev) => {
        if (!prev || !prev.id) return null;
        return taskRows.find((t) => t.id === prev.id) || null;
      });
      setSelectedTaskId((prev) => {
        if (!prev) return taskRows.length === 1 ? taskRows[0].id : prev;
        return taskRows.some((t) => t.id === prev) ? prev : null;
      });
    } catch (e) {
      setTasksError(e.message);
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  };

  const loadPlannerRuns = async (fullChangeId) => {
    if (!fullChangeId) {
      setPlannerRuns([]);
      return;
    }
    setPlannerRunsLoading(true);
    setPlannerRunsError(null);
    try {
      const res = await fetch(`/api/dev-ide/planner/runs?full_change_id=${encodeURIComponent(fullChangeId)}&limit=200&offset=0`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to load planner runs.");
      setPlannerRuns(data.runs || []);
    } catch (e) {
      setPlannerRunsError(e.message);
      setPlannerRuns([]);
    } finally {
      setPlannerRunsLoading(false);
    }
  };

  useEffect(() => {
    loadList();
    loadProjectOptions();
  }, []);

  useEffect(() => {
    if (selectedId) {
      setPlannerCreateError(null);
      setTaskGenerationInfo(null);
      loadOne(selectedId);
      loadLinked(selectedId);
      loadTasks(selectedId);
      loadPlannerRuns(selectedId);
    } else {
      setSelected(null);
      setLinkedChanges([]);
      setTasks([]);
      setSelectedTaskId(null);
      setSelectedTask(null);
      setPlannerRuns([]);
      setPlannerCreateError(null);
      setPlannerGoalText("");
      setTaskGenerationInfo(null);
    }
  }, [selectedId]);

  useEffect(() => {
    if (linkedChanges && linkedChanges.length > 0) {
      loadQueueWorkflowStatuses(linkedChanges);
    } else {
      setQueueWorkflowStatusByChangeId({});
    }
  }, [linkedChanges, selected && selected.project_id]);

  useEffect(() => {
    if (newProjectId) loadModuleOptions(newProjectId, "create");
    else setCreateModuleOptions([]);
  }, [newProjectId]);

  useEffect(() => {
    const projectId = selected && selected.project_id ? selected.project_id : "";
    if (projectId) loadModuleOptions(projectId, "selected");
    else setSelectedModuleOptions([]);
  }, [selected && selected.project_id]);

  useEffect(() => {
    const moduleName = selected && selected.module_name ? String(selected.module_name) : "";
    if (moduleName && !isAllModulesValue(moduleName)) setPlannerModule(moduleName);
  }, [selected && selected.module_name]);

  useEffect(() => {
    const selectedProject = (selected && selected.project_id) || selectedProjectId || "";
    setTargetPreviewSourceProjectId((prev) => prev || pickDefaultTargetPreviewSourceProjectId(projectOptions, selectedProject));
  }, [projectOptions, selected && selected.project_id, selectedProjectId]);

  useEffect(() => {
    setTargetPreviewTargetProjectId((prev) => {
      if (prev && prev !== targetPreviewSourceProjectId) return prev;
      return pickDefaultTargetPreviewTargetProjectId(projectOptions, targetPreviewSourceProjectId);
    });
  }, [projectOptions, targetPreviewSourceProjectId]);

  useEffect(() => {
    setTaskSchemaTargetResult(null);
    setTaskSchemaTargetError(null);
    if (!selectedTaskId) {
      setSelectedTask(null);
      return;
    }
    const match = (tasks || []).find((task) => task.id === selectedTaskId) || null;
    setSelectedTask(match);
  }, [selectedTaskId, tasks]);

  const handleCreate = async () => {
    const title = (newTitle || "").trim();
    if (!title) {
      setError("Title is required.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title,
          description: newDescription || "",
          status: "DRAFT",
          project_id: newProjectId || null,
          module_name: newModuleName || null,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Create failed.");
      }

      setNewTitle("");
      setNewDescription("");
      setNewProjectId("");
      setNewModuleName("");
      await loadList();

      if (data.full_change && data.full_change.id) {
        setSelectedId(data.full_change.id);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSelected = async () => {
    if (!selected || !selected.id) return;

    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: selected.id,
          title: selected.title,
          description: selected.description,
          status: selected.status,
          priority: selected.priority,
          owner: selected.owner,
          project_id: selected.project_id || null,
          module_name: selected.module_name || null,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Update failed.");

      setSelected(data.full_change || selected);
      await loadList();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const resetCreateLinkedForm = () => {
    setCreateLinkedTitle("");
    setCreateLinkedDescription("");
    setCreateLinkedStatus("OPEN");
    setCreateLinkedOrder("");
    setCreateLinkedScopeSummary("");
    setCreateLinkedAllowedFiles("");
    setCreateLinkedImplementationNotes("");
  };

  const handleCreateLinkedDevIdeChange = async () => {
    if (!selectedId) return;

    const title = String(createLinkedTitle || "").trim();
    if (!title) {
      setLinkedError("Change title is required.");
      return;
    }

    const orderVal = String(createLinkedOrder || "").trim();
    const selectedModule = selected && selected.module_name && !isAllModulesValue(selected.module_name)
      ? selected.module_name
      : "";
    const payload = {
      full_change_id: selectedId,
      title,
      description: createLinkedDescription,
      status: createLinkedStatus || "OPEN",
      module: selectedModule || null,
      scope_summary: createLinkedScopeSummary,
      allowed_files: createLinkedAllowedFiles,
      implementation_notes: createLinkedImplementationNotes,
    };
    if (orderVal !== "") {
      payload.full_change_order = orderVal;
    }

    setCreateLinkedLoading(true);
    setLinkedError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/create-linked-dev-ide-change", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Create linked DEV IDE change failed.");
      }

      setLinkedChanges(data.linked_changes || []);
      resetCreateLinkedForm();
    } catch (e) {
      setLinkedError(e.message);
    } finally {
      setCreateLinkedLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedId) return;

    const devId = String(assignDevIdeChangeId || "").trim();
    if (!devId) {
      setLinkedError("dev_ide_change_id is required.");
      return;
    }

    const orderVal = String(assignOrder || "").trim();
    const payload = {
      full_change_id: selectedId,
      dev_ide_change_id: devId,
    };
    if (orderVal !== "") {
      payload.full_change_order = orderVal;
    }

    setLinkedLoading(true);
    setLinkedError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/assign-dev-ide-change", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Assign failed.");
      }

      setAssignDevIdeChangeId("");
      setAssignOrder("");
      await loadLinked(selectedId);
    } catch (e) {
      setLinkedError(e.message);
    } finally {
      setLinkedLoading(false);
    }
  };

  const handleUnassign = async (devIdeChangeId) => {
    if (!devIdeChangeId) return;

    setLinkedLoading(true);
    setLinkedError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/unassign-dev-ide-change", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dev_ide_change_id: devIdeChangeId }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Unassign failed.");
      await loadLinked(selectedId);
    } catch (e) {
      setLinkedError(e.message);
    } finally {
      setLinkedLoading(false);
    }
  };

  const handleCreateTask = async () => {
    if (!selectedId) return;
    const title = String(taskCreateTitle || "").trim();
    if (!title) {
      setTasksError("Task title is required.");
      return;
    }

    setTaskCreateLoading(true);
    setTasksError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_change_id: selectedId,
          title,
          task_type: taskCreateType,
          status: "DRAFT",
          created_by: "full_change_panel",
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Create task failed.");
      }
      setTaskCreateTitle("");
      setTaskCreateType("GENERAL");
      await loadTasks(selectedId);
      if (data.task && data.task.id) {
        setSelectedTaskId(data.task.id);
      }
    } catch (e) {
      setTasksError(e.message);
    } finally {
      setTaskCreateLoading(false);
    }
  };

  const handleSaveTask = async () => {
    if (!selectedTask || !selectedTask.id) return;
    setTaskSaveLoading(true);
    setTasksError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: selectedTask.id,
          title: selectedTask.title,
          description: selectedTask.description,
          task_type: selectedTask.task_type,
          status: selectedTask.status,
          scope_summary: selectedTask.scope_summary,
          allowed_files: selectedTask.allowed_files,
          acceptance_criteria: selectedTask.acceptance_criteria,
          sql_summary: selectedTask.sql_summary,
          sql_proposal: selectedTask.sql_proposal,
          rollback_sql: selectedTask.rollback_sql,
          task_order: selectedTask.task_order,
          updated_by: "full_change_panel",
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Save task failed.");
      }
      setSelectedTask(data.task || selectedTask);
      await loadTasks(selectedId);
    } catch (e) {
      setTasksError(e.message);
    } finally {
      setTaskSaveLoading(false);
    }
  };

  const handleDeleteTask = async () => {
    if (!selectedTask || !selectedTask.id) return;
    setTaskDeleteLoading(true);
    setTasksError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: selectedTask.id }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Delete task failed.");
      const deletedId = selectedTask.id;
      setSelectedTaskId(null);
      setSelectedTask(null);
      await loadTasks(selectedId);
      setTasks((prev) => prev.filter((task) => task.id !== deletedId));
    } catch (e) {
      setTasksError(e.message);
    } finally {
      setTaskDeleteLoading(false);
    }
  };

  const handleGenerateTasks = async () => {
    if (!selectedId) return;
    setTaskGenerateLoading(true);
    setTasksError(null);
    setTaskGenerationInfo(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/generate-drafts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_change_id: selectedId,
          replace_existing: replaceTasksOnGenerate,
          use_llm: useLlmGeneration,
          created_by: "full_change_task_generator",
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Generate tasks failed.");
      }
      setTaskGenerationInfo({
        generationMethod: data.generation_method,
        createdCount: data.created_count,
      });
      await loadTasks(selectedId);
    } catch (e) {
      setTasksError(e.message);
    } finally {
      setTaskGenerateLoading(false);
    }
  };

  const handleRunTaskDiscovery = async () => {
    if (!selectedTask || !selectedTask.id) return;
    setTaskDiscoveryLoading(true);
    setTaskDiscoveryError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/discover-files", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          task_id: selectedTask.id,
          top_n: 8,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "File discovery failed.");
      }
      const findings = (data && data.findings) || {};
      setTaskDiscoveryResult(data);
      setTaskDiscoverySelectedFiles(findings.default_selected_files || []);
      setTaskDiscoveryTargetIds(findings.suggested_target_task_ids || []);
      setTaskDiscoveryDialogOpen(true);
    } catch (e) {
      setTaskDiscoveryError(e.message);
    } finally {
      setTaskDiscoveryLoading(false);
    }
  };

  const toggleDiscoveryFile = (path) => {
    setTaskDiscoverySelectedFiles((prev) => {
      const items = Array.isArray(prev) ? prev.slice() : [];
      return items.includes(path) ? items.filter((p) => p !== path) : [...items, path];
    });
  };

  const toggleDiscoveryTargetTask = (taskId) => {
    setTaskDiscoveryTargetIds((prev) => {
      const items = Array.isArray(prev) ? prev.slice() : [];
      return items.includes(taskId) ? items.filter((id) => id !== taskId) : [...items, taskId];
    });
  };

  const handleApplyTaskDiscovery = async () => {
    if (!selectedTask || !selectedTask.id) return;
    if (!taskDiscoverySelectedFiles || taskDiscoverySelectedFiles.length === 0) {
      setTaskDiscoveryError("Select at least one approved file.");
      return;
    }
    setTaskDiscoveryApplyLoading(true);
    setTaskDiscoveryError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/apply-discovery", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source_task_id: selectedTask.id,
          approved_files: taskDiscoverySelectedFiles,
          target_task_ids: taskDiscoveryTargetIds,
          discovery_summary: ((taskDiscoveryResult && taskDiscoveryResult.findings && taskDiscoveryResult.findings.summary) || "") + ((taskDiscoveryResult && taskDiscoveryResult.findings && taskDiscoveryResult.findings.recommended_primary_file) ? `\nRecommended primary file: ${taskDiscoveryResult.findings.recommended_primary_file}` : ""),
          updated_by: "full_change_panel_discovery",
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Applying discovery findings failed.");
      }
      await loadTasks(selectedId);
      setTaskDiscoveryDialogOpen(false);
      setTaskDiscoveryResult(null);
      setTaskDiscoverySelectedFiles([]);
      setTaskDiscoveryTargetIds([]);
    } catch (e) {
      setTaskDiscoveryError(e.message);
    } finally {
      setTaskDiscoveryApplyLoading(false);
    }
  };

  const handleRunTaskPatcher = async () => {
    if (!selectedTask || !selectedTask.id) return;
    setTaskPatchPreviewLoading(true);
    setTaskPatchPreviewError(null);
    setTaskPatchApplyResult(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/build-patch-preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          task_id: selectedTask.id,
          max_tokens: 6000,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Patch preview failed.");
      }
      setTaskPatchPreviewResult(data);
      setTaskPatchDialogOpen(true);
    } catch (e) {
      setTaskPatchPreviewError(e.message);
    } finally {
      setTaskPatchPreviewLoading(false);
    }
  };

  const handleApplyTaskPatch = async () => {
    if (!selectedTask || !selectedTask.id || !taskPatchPreviewResult?.preview?.previews) return;
    setTaskPatchApplyLoading(true);
    setTaskPatchPreviewError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/apply-patch-preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          task_id: selectedTask.id,
          previews: taskPatchPreviewResult.preview.previews,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Apply patch failed.");
      }
      setTaskPatchApplyResult(data.apply_result || null);
      await loadTasks(selectedId);
      if (data.task && data.task.id) {
        setSelectedTaskId(data.task.id);
      }
    } catch (e) {
      setTaskPatchPreviewError(e.message);
    } finally {
      setTaskPatchApplyLoading(false);
    }
  };


  const handleResolveTaskSchemaTarget = async () => {
    if (!selectedTask || !selectedTask.id) return;
    setTaskSchemaTargetLoading(true);
    setTaskSchemaTargetError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/resolve-schema-target", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ task_id: selectedTask.id }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Schema target resolution failed.");
      }
      setTaskSchemaTargetResult(data.schema_target || null);
    } catch (e) {
      setTaskSchemaTargetError(e.message);
      setTaskSchemaTargetResult(null);
    } finally {
      setTaskSchemaTargetLoading(false);
    }
  };

  const handleRunTaskSqlProposal = async () => {
    if (!selectedTask || !selectedTask.id) return;
    setTaskSqlProposalLoading(true);
    setTaskSqlProposalError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/generate-sql-proposal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          task_id: selectedTask.id,
          use_llm: true,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "SQL proposal generation failed.");
      }
      const proposal = data.proposal || {};
      setTaskSqlProposalResult(data);
      setTaskSchemaTargetResult(data.schema_target || taskSchemaTargetResult || null);
      setTaskSqlSummaryDraft(proposal.summary || "");
      setTaskSqlProposalDraft(proposal.sql_proposal || "");
      setTaskRollbackSqlDraft(proposal.rollback_sql || "");
      setTaskSqlDialogOpen(true);
    } catch (e) {
      setTaskSqlProposalError(e.message);
    } finally {
      setTaskSqlProposalLoading(false);
    }
  };

  const handleApplyTaskSqlProposal = async () => {
    if (!selectedTask || !selectedTask.id) return;
    if (!String(taskSqlProposalDraft || "").trim()) {
      setTaskSqlProposalError("SQL proposal is required.");
      return;
    }
    setTaskSqlApplyLoading(true);
    setTaskSqlProposalError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/apply-sql-proposal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          task_id: selectedTask.id,
          sql_summary: taskSqlSummaryDraft,
          sql_proposal: taskSqlProposalDraft,
          rollback_sql: taskRollbackSqlDraft,
          status: String((selectedTask && selectedTask.status) || "").toUpperCase() === "DRAFT" ? "PROPOSED" : selectedTask.status,
          updated_by: "full_change_sql_panel",
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Saving SQL proposal failed.");
      }
      await loadTasks(selectedId);
      if (data.task && data.task.id) {
        setSelectedTaskId(data.task.id);
      }
      setTaskSqlDialogOpen(false);
    } catch (e) {
      setTaskSqlProposalError(e.message);
    } finally {
      setTaskSqlApplyLoading(false);
    }
  };

  const handleOpenTaskSqlRunDialog = async () => {
    if (!selectedTask || !selectedTask.id) return;
    setTaskSqlRunDialogOpen(true);
    setTaskSqlRunLoading(true);
    setTaskSqlRunError(null);
    setTaskSqlRunExecution(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/run-sql-proposal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          task_id: selectedTask.id,
          dry_run: true,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "SQL run preview failed.");
      }
      setTaskSqlRunPreview(data.preview || null);
      setTaskSqlRunExecution(data.execution || null);
    } catch (e) {
      setTaskSqlRunError(e.message);
    } finally {
      setTaskSqlRunLoading(false);
    }
  };

  const handleExecuteTaskSqlProposal = async () => {
    if (!selectedTask || !selectedTask.id) return;
    setTaskSqlRunLoading(true);
    setTaskSqlRunError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/run-sql-proposal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          task_id: selectedTask.id,
          dry_run: false,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "SQL execution failed.");
      }
      setTaskSqlRunPreview(data.preview || null);
      setTaskSqlRunExecution(data.execution || null);
      await loadTasks(selectedId);
    } catch (e) {
      setTaskSqlRunError(e.message);
    } finally {
      setTaskSqlRunLoading(false);
    }
  };

  const handleCreatePlannerRun = async () => {
    if (!selectedId) return;

    const mod = String(plannerModule || "").trim();
    if (!mod) {
      setPlannerCreateError("Module is required.");
      return;
    }
    if (isAllModulesValue(mod)) {
      setPlannerCreateError("Planner advisory lane requires a specific module. Use task-level file discovery for project-wide scope.");
      return;
    }

    const goal = String(plannerGoalText || "").trim();
    if (!goal) {
      setPlannerCreateError("Planner goal text is required.");
      return;
    }

    setPlannerCreateLoading(true);
    setPlannerCreateError(null);
    try {
      const sessRes = await fetch("/api/dev-ide/planner/sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          module: mod,
          full_change_id: selectedId,
        }),
      });
      const sessData = await sessRes.json();
      if (!sessData.success) {
        throw new Error((sessData.errors && sessData.errors.join(", ")) || sessData.error || "Create planner session failed.");
      }

      const plannerSessionId = sessData.planner_session_id ?? sessData.session_id ?? sessData.sessionId ?? (sessData.session && sessData.session.id) ?? null;
      if (plannerSessionId === null || plannerSessionId === undefined || plannerSessionId === "") {
        throw new Error("Create planner session did not return planner_session_id.");
      }

      const res = await fetch("/api/dev-ide/planner/runs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          planner_session_id: plannerSessionId,
          full_change_id: selectedId,
          module: mod,
          status: "DRAFT",
          goal_text: goal,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Create planner run failed.");
      }

      try {
        const contextText = buildFullChangeContextText(selected, linkedChanges, goal);
        await fetch("/api/dev-ide/planner/goals", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            planner_session_id: plannerSessionId,
            category: "FULL_CHANGE",
            description: "Full Change context captured at run creation time.",
            active: true,
            goal_text: contextText,
          }),
        });
      } catch (e) {
        // non-blocking
      }

      await loadPlannerRuns(selectedId);
    } catch (e) {
      setPlannerCreateError(e.message);
    } finally {
      setPlannerCreateLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, height: "100%", minHeight: 0, overflow: { xs: "auto", md: "hidden" } }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ height: { xs: "auto", md: "100%" }, minHeight: 0, overflow: { xs: "visible", md: "hidden" } }}
      >
        <Paper
          sx={{
            width: { xs: "100%", md: 360 },
            flexShrink: 0,
            p: 2,
            overflowY: "auto",
            maxHeight: { xs: "45vh", md: "100%" },
          }}
        >
          <Typography variant="h6">Full Change</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
            Governing change record with child execution tasks underneath it.
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle2">Create</Typography>
          <TextField size="small" fullWidth label="Title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} sx={{ mt: 1 }} />
          <TextField
            size="small"
            fullWidth
            label="Description"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            sx={{ mt: 1 }}
            multiline
            minRows={3}
          />
          <TextField
            size="small"
            select
            fullWidth
            label="Project"
            value={newProjectId}
            onChange={(e) => {
              setNewProjectId(e.target.value);
              setNewModuleName("");
            }}
            sx={{ mt: 1 }}
            helperText={projectOptionsError || "Optional but recommended."}
          >
            <MenuItem value="">None</MenuItem>
            {(projectOptions || []).map((project) => (
              <MenuItem key={project.project_id} value={project.project_id}>
                {project.label || project.project_id}
              </MenuItem>
            ))}
          </TextField>
          {createModuleOptions && createModuleOptions.length > 0 ? (
            <TextField
              size="small"
              select
              fullWidth
              label="Module"
              value={newModuleName}
              onChange={(e) => setNewModuleName(e.target.value)}
              sx={{ mt: 1 }}
              disabled={!newProjectId}
              helperText={!newProjectId ? "Select a project first." : "Module within the selected project."}
            >
              <MenuItem value="">None</MenuItem>
              {createModuleOptions.map((row) => {
                const moduleName = getModuleOptionValue(row);
                return (
                  <MenuItem key={moduleName} value={moduleName}>
                    {getModuleOptionLabel(row)}
                  </MenuItem>
                );
              })}
            </TextField>
          ) : (
            <TextField
              size="small"
              fullWidth
              label="Module"
              value={newModuleName}
              onChange={(e) => setNewModuleName(e.target.value)}
              sx={{ mt: 1 }}
              disabled={!newProjectId}
              helperText={!newProjectId ? "Select a project first." : (moduleOptionsLoading ? "Loading modules..." : "Enter module name if not listed.")}
            />
          )}
          <Button variant="contained" onClick={handleCreate} sx={{ mt: 1 }} disabled={loading || projectOptionsLoading}>
            {loading ? "Working..." : "Create Full Change"}
          </Button>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Existing</Typography>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          )}

          {error && (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {error}
            </Typography>
          )}

          {(items || []).map((it) => (
            <Box
              key={it.id}
              onClick={() => setSelectedId(it.id)}
              sx={{
                p: 1,
                borderRadius: 1,
                cursor: "pointer",
                border: "1px solid rgba(0,0,0,0.12)",
                mb: 1,
                backgroundColor: selectedId === it.id ? "rgba(25,118,210,0.08)" : "transparent",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                #{it.id} {it.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {it.status || "DRAFT"}
                {it.project_id ? ` • ${it.project_id}` : ""}
                {it.module_name ? ` • ${getModuleOptionLabel(it.module_name)}` : ""}
              </Typography>
            </Box>
          ))}
        </Paper>

        <Paper
          sx={{
            flex: 1,
            minWidth: 0,
            p: { xs: 1.5, md: 2 },
            display: "flex",
            flexDirection: "column",
            minHeight: { xs: "auto", md: 0 },
            overflowY: "auto",
          }}
        >
          {!selectedId ? (
            <Typography variant="body2" color="text.secondary">
              Select a Full Change on the left.
            </Typography>
          ) : (
            <>
              <Typography variant="h6" sx={{ mb: 1 }}>Full Change Details</Typography>

              <TextField
                size="small"
                fullWidth
                label="Title"
                value={(selected && selected.title) || ""}
                onChange={(e) => setSelected((prev) => ({ ...(prev || {}), title: e.target.value }))}
                sx={{ mb: 1 }}
              />

              <TextField
                size="small"
                fullWidth
                label="Description"
                value={(selected && selected.description) || ""}
                onChange={(e) => setSelected((prev) => ({ ...(prev || {}), description: e.target.value }))}
                sx={{ mb: 1 }}
                multiline
                minRows={4}
              />

              <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1 }}>
                <TextField
                  size="small"
                  select
                  fullWidth
                  label="Project"
                  value={(selected && selected.project_id) || ""}
                  onChange={(e) => setSelected((prev) => ({ ...(prev || {}), project_id: e.target.value, module_name: "" }))}
                  helperText={projectOptionsError || "Select the target project first."}
                >
                  <MenuItem value="">None</MenuItem>
                  {(projectOptions || []).map((project) => (
                    <MenuItem key={project.project_id} value={project.project_id}>
                      {project.label || project.project_id}
                    </MenuItem>
                  ))}
                </TextField>
                {selectedModuleOptions && selectedModuleOptions.length > 0 ? (
                  <TextField
                    size="small"
                    select
                    fullWidth
                    label="Module"
                    value={(selected && selected.module_name) || ""}
                    onChange={(e) => setSelected((prev) => ({ ...(prev || {}), module_name: e.target.value }))}
                    helperText={!selected?.project_id ? "Select a project first." : "Module within the selected project."}
                    disabled={!selected?.project_id}
                  >
                    <MenuItem value="">None</MenuItem>
                    {selectedModuleOptions.map((row) => {
                      const moduleName = getModuleOptionValue(row);
                      return (
                        <MenuItem key={moduleName} value={moduleName}>
                          {getModuleOptionLabel(row)}
                        </MenuItem>
                      );
                    })}
                    {(selected && selected.module_name && !selectedModuleOptions.some((row) => getModuleOptionValue(row) === selected.module_name)) ? (
                      <MenuItem value={selected.module_name}>{getModuleOptionLabel(selected.module_name)}</MenuItem>
                    ) : null}
                  </TextField>
                ) : (
                  <TextField
                    size="small"
                    fullWidth
                    label="Module"
                    value={(selected && selected.module_name) || ""}
                    onChange={(e) => setSelected((prev) => ({ ...(prev || {}), module_name: e.target.value }))}
                    helperText={!selected?.project_id ? "Select a project first." : (moduleOptionsLoading ? "Loading modules..." : "Enter module name if not listed.")}
                    disabled={!selected?.project_id}
                  />
                )}
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Status"
                  value={(selected && selected.status) || "DRAFT"}
                  onChange={(e) => setSelected((prev) => ({ ...(prev || {}), status: e.target.value }))}
                  helperText="DRAFT | PLANNED | IN_PROGRESS | READY_FOR_TEST | APPROVED | RELEASED"
                />
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Priority"
                  value={(selected && selected.priority) || ""}
                  onChange={(e) => setSelected((prev) => ({ ...(prev || {}), priority: e.target.value }))}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Owner"
                  value={(selected && selected.owner) || ""}
                  onChange={(e) => setSelected((prev) => ({ ...(prev || {}), owner: e.target.value }))}
                />
              </Stack>

              <Stack direction="row" spacing={1} useFlexGap sx={{ mb: 1, flexWrap: "wrap" }}>
                <Button variant="contained" onClick={handleUpdateSelected} disabled={loading} sx={{ width: { xs: "100%", sm: "auto" } }}>
                  {loading ? "Working..." : "Save"}
                </Button>
                <Button variant="outlined" onClick={() => loadLinked(selectedId)} disabled={linkedLoading} sx={{ width: { xs: "100%", sm: "auto" } }}>
                  Refresh Linked Changes
                </Button>
                <Button variant="outlined" onClick={handleExportPlanningPackage} disabled={planningExportLoading || loading} sx={{ width: { xs: "100%", sm: "auto" } }}>
                  {planningExportLoading ? "Exporting planning ZIP..." : "Export Planning Package for GPT"}
                </Button>
                <Button variant="outlined" component="label" disabled={planningImportLoading || loading} sx={{ width: { xs: "100%", sm: "auto" } }}>
                  {planningImportLoading ? "Validating planning ZIP..." : "Import Planning Package from GPT"}
                  <input type="file" hidden accept=".zip,application/zip" onChange={handlePlanningImportFileSelected} />
                </Button>
                <Button variant="outlined" onClick={() => loadTasks(selectedId)} disabled={tasksLoading} sx={{ width: { xs: "100%", sm: "auto" } }}>
                  Refresh Tasks
                </Button>
                <Button variant="outlined" onClick={() => loadPlannerRuns(selectedId)} disabled={plannerRunsLoading} sx={{ width: { xs: "100%", sm: "auto" } }}>
                  Refresh Planner Runs
                </Button>
              </Stack>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Planning ZIPs are for Full Change structure only.
                </Typography>
                <Typography variant="body2">
                  Use Export/Import Planning Package for GPT to propose or update the parent Full Change, linked DEV IDE child changes, and governance tasks. Use the normal child DEV IDE implementation package workflow for code or document file changes.
                </Typography>
              </Alert>

              <Divider sx={{ mb: 2 }} />

              <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: "primary.light", backgroundColor: "rgba(25,118,210,0.03)" }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ justifyContent: "space-between", alignItems: { xs: "stretch", md: "center" }, mb: 1 }}>
                  <Box>
                    <Typography variant="h6">Linked DEV IDE Changes — Implementation Queue</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Each linked DEV IDE change is a smaller rollbackable implementation package under this Full Change. Export, implement, import, verify, accept, or roll back each package independently without changing the parent Full Change workflow.
                    </Typography>
                  </Box>
                  <Button variant="outlined" onClick={() => loadLinked(selectedId)} disabled={linkedLoading}>
                    Refresh Queue
                  </Button>
                </Stack>

                <Paper variant="outlined" sx={{ p: 1.5, mt: 1, mb: 1, backgroundColor: "rgba(255,255,255,0.72)" }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Create Linked DEV IDE Change</Typography>
                  <Stack spacing={1}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                      <TextField size="small" label="Change title" value={createLinkedTitle} onChange={(e) => setCreateLinkedTitle(e.target.value)} fullWidth sx={{ flex: 1 }} />
                      <TextField size="small" select label="Status" value={createLinkedStatus} onChange={(e) => setCreateLinkedStatus(e.target.value)} fullWidth sx={{ minWidth: { xs: "100%", md: 180 } }}>
                        {DEV_IDE_CHANGE_STATUSES.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
                      </TextField>
                      <TextField size="small" label="Order" value={createLinkedOrder} onChange={(e) => setCreateLinkedOrder(e.target.value)} placeholder={String(getNextLinkedChangeOrder())} fullWidth sx={{ width: { xs: "100%", md: 120 } }} />
                    </Stack>
                    <TextField size="small" multiline minRows={2} label="Change description" value={createLinkedDescription} onChange={(e) => setCreateLinkedDescription(e.target.value)} fullWidth />
                    <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                      <TextField size="small" multiline minRows={2} label="Scope summary (optional)" value={createLinkedScopeSummary} onChange={(e) => setCreateLinkedScopeSummary(e.target.value)} fullWidth sx={{ flex: 1 }} />
                      <TextField size="small" multiline minRows={2} label="Allowed files (optional)" value={createLinkedAllowedFiles} onChange={(e) => setCreateLinkedAllowedFiles(e.target.value)} fullWidth sx={{ flex: 1 }} />
                      <TextField size="small" multiline minRows={2} label="Implementation notes (optional)" value={createLinkedImplementationNotes} onChange={(e) => setCreateLinkedImplementationNotes(e.target.value)} fullWidth sx={{ flex: 1 }} />
                    </Stack>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap sx={{ flexWrap: "wrap", alignItems: { xs: "stretch", sm: "center" } }}>
                      <Button variant="contained" onClick={handleCreateLinkedDevIdeChange} disabled={linkedLoading || createLinkedLoading} sx={{ width: { xs: "100%", sm: "auto" } }}>
                        {createLinkedLoading ? "Creating..." : "Create Linked DEV IDE Change"}
                      </Button>
                      <Button variant="text" onClick={resetCreateLinkedForm} disabled={createLinkedLoading} sx={{ width: { xs: "100%", sm: "auto" } }}>Clear</Button>
                    </Stack>
                  </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.5, mt: 1, mb: 1, backgroundColor: "rgba(255,255,255,0.72)" }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Assign Existing to Queue</Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap sx={{ flexWrap: "wrap", alignItems: { xs: "stretch", sm: "center" } }}>
                    <TextField size="small" label="existing dev_ide_change_id" value={assignDevIdeChangeId} onChange={(e) => setAssignDevIdeChangeId(e.target.value)} fullWidth sx={{ width: { xs: "100%", sm: 220 } }} />
                    <TextField size="small" label="order (optional)" value={assignOrder} onChange={(e) => setAssignOrder(e.target.value)} fullWidth sx={{ width: { xs: "100%", sm: 160 } }} />
                    <Button variant="outlined" onClick={handleAssign} disabled={linkedLoading} sx={{ width: { xs: "100%", sm: "auto" } }}>Assign Existing to Queue</Button>
                  </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.5, mt: 1, mb: 1, backgroundColor: "rgba(255,255,255,0.72)" }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Aggregate Preview to Target</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    Preview the target migration impact across all linked DEV IDE child changes, then install eligible changed files into the selected target project. Install recomputes a fresh aggregate preview first and blocks duplicate changed target paths before any write occurs.
                  </Typography>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1} useFlexGap sx={{ flexWrap: "wrap", alignItems: { xs: "stretch", md: "center" } }}>
                    <TextField
                      size="small"
                      select
                      label="Source project"
                      value={targetPreviewSourceProjectId}
                      onChange={(e) => setTargetPreviewSourceProjectId(e.target.value)}
                      fullWidth
                      sx={{ minWidth: { xs: "100%", md: 220 }, flex: 1 }}
                    >
                      <MenuItem value="">Select source</MenuItem>
                      {(projectOptions || []).map((project) => (
                        <MenuItem key={`target-preview-source-${project.project_id}`} value={project.project_id}>
                          {project.label || project.project_id}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      select
                      label="Target project"
                      value={targetPreviewTargetProjectId}
                      onChange={(e) => setTargetPreviewTargetProjectId(e.target.value)}
                      fullWidth
                      sx={{ minWidth: { xs: "100%", md: 220 }, flex: 1 }}
                    >
                      <MenuItem value="">Select target</MenuItem>
                      {(projectOptions || []).map((project) => (
                        <MenuItem key={`target-preview-target-${project.project_id}`} value={project.project_id} disabled={project.project_id === targetPreviewSourceProjectId}>
                          {project.label || project.project_id}
                        </MenuItem>
                      ))}
                    </TextField>
                    <FormControlLabel
                      control={<Checkbox checked={targetPreviewIncludeSql} onChange={(e) => setTargetPreviewIncludeSql(e.target.checked)} />}
                      label="Include SQL"
                      sx={{ mx: 0 }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleRunTargetPreview}
                      disabled={targetPreviewLoading || targetInstallLoading || linkedLoading || !selectedId || !targetPreviewSourceProjectId || !targetPreviewTargetProjectId || targetPreviewSourceProjectId === targetPreviewTargetProjectId}
                      sx={{ width: { xs: "100%", md: "auto" } }}
                    >
                      {targetPreviewLoading ? "Previewing…" : "Preview to Target"}
                    </Button>
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={handleInstallTargetMigration}
                      disabled={targetPreviewLoading || targetInstallLoading || linkedLoading || !selectedId || !targetPreviewSourceProjectId || !targetPreviewTargetProjectId || targetPreviewSourceProjectId === targetPreviewTargetProjectId}
                      sx={{ width: { xs: "100%", md: "auto" } }}
                    >
                      {targetInstallLoading ? "Installing…" : "Install to Target"}
                    </Button>
                  </Stack>
                </Paper>

                {linkedError && (
                  <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                    {linkedError}
                  </Typography>
                )}
                {queueWorkflowStatusError && (
                  <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
                    Queue workflow status is using local state because DB sync failed: {queueWorkflowStatusError}
                  </Alert>
                )}
                {queueActionMessage ? (
                  <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
                    {queueActionMessage}
                  </Alert>
                ) : null}

                <Box sx={{ border: "1px solid rgba(25,118,210,0.20)", borderRadius: 1, overflowX: "auto", maxWidth: "100%", backgroundColor: "background.paper" }}>
                  {linkedLoading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                      <CircularProgress size={20} />
                    </Box>
                  )}

                  {!linkedLoading && (!linkedChanges || linkedChanges.length === 0) && (
                    <Box sx={{ p: 1.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        No linked DEV IDE changes yet.
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        Create or assign child DEV IDE changes here, or import a GPT planning ZIP to propose rollbackable child implementation units. Planning imports only update planning records; code and document implementation ZIPs are still imported through each child DEV IDE change workflow.
                      </Typography>
                    </Box>
                  )}

                  {!linkedLoading && linkedChanges && linkedChanges.length > 0 && (
                    <>
                      <Stack spacing={1.25} sx={{ display: { xs: "flex", md: "none" }, p: 1 }}>
                        {(linkedChanges || []).map((c, idx) => {
                          const changedFilesCount = c.changed_files_count ?? c.changed_file_count ?? c.file_count ?? c.files_count;
                          const rowWorkflowStatus = mergeQueueWorkflowStatus(queueWorkflowStatusByChangeId[c.id]);
                          const exportStatus = getQueueStatusFromWorkflowStep(rowWorkflowStatus.packageExported);
                          const importStatus = getQueueStatusFromWorkflowStep(rowWorkflowStatus.packageImported);
                          const saveApplyStatus = getQueueStatusFromWorkflowStep(rowWorkflowStatus.changedFilesSaved);
                          const frontendStatus = getQueueStatusFromWorkflowStep(rowWorkflowStatus.frontendBuildCompleted);
                          const backendStatus = getQueueStatusFromWorkflowStep(rowWorkflowStatus.backendRestartCompleted);
                          const verificationStatus = frontendStatus === "Success" && backendStatus === "Success" ? "Success" : frontendStatus === "Failed" || backendStatus === "Failed" ? "Failed" : frontendStatus === "Running" || backendStatus === "Running" ? "Running" : "Pending";
                          const humanReviewStatus = c.human_review_status ?? c.review_status ?? c.acceptance_status ?? getQueueStatusFromWorkflowStep(rowWorkflowStatus.verificationSummaryCopied);
                          const workflowProjectId = `Project: ${getQueueProjectId(c)} · Change #${c.id}`;
                          const rowLoadingExport = queueActionLoadingId === `export-${c.id}`;
                          const rowLoadingImport = queueActionLoadingId === `import-${c.id}`;
                          const rowLoadingSummary = queueActionLoadingId === `summary-${c.id}`;
                          const unavailableText = "Not available inline yet. Use Development tab / Import Review for this existing workflow action.";
                          const mobileStatusRows = [
                            ["Export", exportStatus, "Export package workflow status"],
                            ["Import", importStatus, "Import package workflow status"],
                            ["Save/apply", saveApplyStatus, "Save/apply workflow status"],
                            ["Verification", verificationStatus, "Frontend/backend verification workflow status"],
                            ["Human review", humanReviewStatus, "Human review workflow status"],
                          ];

                          return (
                            <Paper key={c.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                              <Stack spacing={1}>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Order {c.full_change_order ?? idx + 1} · Change #{c.id}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    {c.title || "Untitled change"}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Rollbackable child implementation package
                                  </Typography>
                                </Box>

                                <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap" }}>
                                  <Chip size="small" label={`Status: ${c.status || "—"}`} variant="outlined" />
                                  <Chip size="small" label={`Files: ${changedFilesCount ?? "—"}`} variant="outlined" />
                                </Stack>

                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: "minmax(86px, 0.9fr) minmax(120px, 1.1fr)",
                                    gap: 0.75,
                                    alignItems: "center",
                                  }}
                                >
                                  {mobileStatusRows.map(([label, status, title]) => (
                                    <React.Fragment key={label}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                        {label}
                                      </Typography>
                                      <Box sx={{ minWidth: 0 }}>{renderQueueStatusChip(status, `${workflowProjectId} · ${title}`)}</Box>
                                    </React.Fragment>
                                  ))}
                                </Box>

                                <Typography variant="caption" color="text.secondary">
                                  Active linked change context: #{c.id} — {c.title || "Untitled change"}
                                </Typography>

                                <Stack spacing={0.75}>
                                  <Button size="small" variant="contained" onClick={() => handleQueueExportPackage(c)} disabled={Boolean(queueActionLoadingId)} fullWidth>
                                    {rowLoadingExport ? "Exporting..." : "Export Agent Package"}
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleQueueImportReturnedPackage(c)}
                                    disabled={Boolean(queueActionLoadingId)}
                                    fullWidth
                                  >
                                    {rowLoadingImport ? "Opening import..." : "Import Returned Package"}
                                  </Button>
                                  <Button size="small" variant="outlined" onClick={() => handleQueueCopyResultSummary(c)} disabled={Boolean(queueActionLoadingId)} fullWidth>
                                    {rowLoadingSummary ? "Copying..." : "Copy Result Summary"}
                                  </Button>
                                  <Button size="small" variant="text" onClick={() => handleUnassign(c.id)} disabled={linkedLoading || Boolean(queueActionLoadingId)} fullWidth>
                                    Unassign
                                  </Button>
                                </Stack>

                                <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap" }}>
                                  <Box component="span" title={unavailableText} sx={{ flex: "1 1 140px" }}>
                                    <Button size="small" variant="outlined" disabled fullWidth>Save Changed Files</Button>
                                  </Box>
                                  <Box component="span" title={unavailableText} sx={{ flex: "1 1 140px" }}>
                                    <Button size="small" variant="outlined" disabled fullWidth>Run Verification</Button>
                                  </Box>
                                  <Box component="span" title={unavailableText} sx={{ flex: "1 1 140px" }}>
                                    <Button size="small" variant="outlined" disabled fullWidth>Open Import Review</Button>
                                  </Box>
                                </Stack>

                                <Typography variant="caption" color="text.secondary">
                                  Import Returned Package opens the existing Development tab import review workflow in this linked change context. Save, verification, and review actions remain in the existing Import Review flow until exposed safely here.
                                </Typography>
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Stack>

                      <Box sx={{ display: { xs: "none", md: "block" }, overflowX: "auto" }}>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "70px minmax(240px, 1.4fr) 110px 80px 120px 120px 120px 135px 135px minmax(520px, 1fr)",
                            gap: 1,
                            px: 1,
                            py: 0.75,
                            borderBottom: "1px solid rgba(0,0,0,0.12)",
                            backgroundColor: "rgba(0,0,0,0.03)",
                            minWidth: 1650,
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>Order</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>Change title</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>Status</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>Files</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>Export</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>Import</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>Save/apply</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>Verification</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>Human review</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>Inline Agent Workflow actions</Typography>
                        </Box>

                        {(linkedChanges || []).map((c, idx) => {
                          const changedFilesCount = c.changed_files_count ?? c.changed_file_count ?? c.file_count ?? c.files_count;
                          const rowWorkflowStatus = mergeQueueWorkflowStatus(queueWorkflowStatusByChangeId[c.id]);
                          const exportStatus = getQueueStatusFromWorkflowStep(rowWorkflowStatus.packageExported);
                          const importStatus = getQueueStatusFromWorkflowStep(rowWorkflowStatus.packageImported);
                          const saveApplyStatus = getQueueStatusFromWorkflowStep(rowWorkflowStatus.changedFilesSaved);
                          const frontendStatus = getQueueStatusFromWorkflowStep(rowWorkflowStatus.frontendBuildCompleted);
                          const backendStatus = getQueueStatusFromWorkflowStep(rowWorkflowStatus.backendRestartCompleted);
                          const verificationStatus = frontendStatus === "Success" && backendStatus === "Success" ? "Success" : frontendStatus === "Failed" || backendStatus === "Failed" ? "Failed" : frontendStatus === "Running" || backendStatus === "Running" ? "Running" : "Pending";
                          const humanReviewStatus = c.human_review_status ?? c.review_status ?? c.acceptance_status ?? getQueueStatusFromWorkflowStep(rowWorkflowStatus.verificationSummaryCopied);
                          const workflowProjectId = `Project: ${getQueueProjectId(c)} · Change #${c.id}`;
                          const rowLoadingExport = queueActionLoadingId === `export-${c.id}`;
                          const rowLoadingImport = queueActionLoadingId === `import-${c.id}`;
                          const rowLoadingSummary = queueActionLoadingId === `summary-${c.id}`;
                          const unavailableText = "Not available inline yet. Use Development tab / Import Review for this existing workflow action.";

                          return (
                            <Box
                              key={c.id}
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "70px minmax(240px, 1.4fr) 110px 80px 120px 120px 120px 135px 135px minmax(520px, 1fr)",
                                gap: 1,
                                px: 1,
                                py: 1,
                                borderBottom: "1px solid rgba(0,0,0,0.08)",
                                alignItems: "center",
                                minWidth: 1650,
                              }}
                            >
                              <Typography variant="body2">{c.full_change_order ?? idx + 1}</Typography>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Change #{c.id} — {c.title || "Untitled change"}</Typography>
                                <Typography variant="caption" color="text.secondary">Rollbackable child implementation package</Typography>
                              </Box>
                              <Typography variant="body2">{c.status || "—"}</Typography>
                              <Typography variant="body2">{changedFilesCount ?? "—"}</Typography>
                              {renderQueueStatusChip(exportStatus, workflowProjectId || "Export package workflow status")}
                              {renderQueueStatusChip(importStatus, workflowProjectId || "Import package workflow status")}
                              {renderQueueStatusChip(saveApplyStatus, workflowProjectId || "Save/apply workflow status")}
                              {renderQueueStatusChip(verificationStatus, workflowProjectId || "Frontend/backend verification workflow status")}
                              {renderQueueStatusChip(humanReviewStatus, workflowProjectId || "Human review workflow status")}
                              <Stack spacing={0.75}>
                                <Typography variant="caption" color="text.secondary">
                                  Active linked change context: #{c.id} — {c.title || "Untitled change"}
                                </Typography>
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                  <Button size="small" variant="contained" onClick={() => handleQueueExportPackage(c)} disabled={Boolean(queueActionLoadingId)}>
                                    {rowLoadingExport ? "Exporting..." : "Export Agent Package"}
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleQueueImportReturnedPackage(c)}
                                    disabled={Boolean(queueActionLoadingId)}
                                  >
                                    {rowLoadingImport ? "Opening import..." : "Import Returned Package"}
                                  </Button>
                                  <Box component="span" title={unavailableText}>
                                    <Button size="small" variant="outlined" disabled>Save Changed Files</Button>
                                  </Box>
                                  <Box component="span" title={unavailableText}>
                                    <Button size="small" variant="outlined" disabled>Run Verification</Button>
                                  </Box>
                                  <Box component="span" title={unavailableText}>
                                    <Button size="small" variant="outlined" disabled>Open Import Review</Button>
                                  </Box>
                                  <Button size="small" variant="outlined" onClick={() => handleQueueCopyResultSummary(c)} disabled={Boolean(queueActionLoadingId)}>
                                    {rowLoadingSummary ? "Copying..." : "Copy Result Summary"}
                                  </Button>
                                  <Button size="small" variant="text" onClick={() => handleUnassign(c.id)} disabled={linkedLoading || Boolean(queueActionLoadingId)}>Unassign</Button>
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                  Import Returned Package opens the existing Development tab import review workflow in this linked change context. Save, verification, and review actions remain in the existing Import Review flow until exposed safely here.
                                </Typography>
                              </Stack>
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  )}

                </Box>
              </Paper>

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle1">Governance / Planning / Checklist Tasks</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Use tasks for optional governance, planning, checklist, analysis, testing, documentation, release, or reviewer work. The linked DEV IDE changes above are the primary rollbackable implementation units.
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1, flexWrap: "wrap" }}>
                <TextField
                  size="small"
                  label="New task title"
                  value={taskCreateTitle}
                  onChange={(e) => setTaskCreateTitle(e.target.value)}
                  sx={{ minWidth: 260 }}
                />
                <TextField
                  size="small"
                  select
                  label="Type"
                  value={taskCreateType}
                  onChange={(e) => setTaskCreateType(e.target.value)}
                  sx={{ minWidth: 160 }}
                >
                  {TASK_TYPES.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </TextField>
                <Button variant="outlined" onClick={handleCreateTask} disabled={taskCreateLoading}>
                  {taskCreateLoading ? "Working..." : "Create Task"}
                </Button>
              </Stack>

              <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap", mb: 1 }}>
                <FormControlLabel
                  control={<Checkbox checked={replaceTasksOnGenerate} onChange={(e) => setReplaceTasksOnGenerate(e.target.checked)} />}
                  label="Replace existing tasks"
                />
                <FormControlLabel
                  control={<Checkbox checked={useLlmGeneration} onChange={(e) => setUseLlmGeneration(e.target.checked)} />}
                  label="Use LLM generation"
                />
                <Button variant="contained" onClick={handleGenerateTasks} disabled={taskGenerateLoading}>
                  {taskGenerateLoading ? "Generating..." : "Generate Draft Tasks"}
                </Button>
              </Stack>

              {taskGenerationInfo && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Generated {taskGenerationInfo.createdCount} task(s) via {taskGenerationInfo.generationMethod}.
                </Typography>
              )}

              {tasksError && (
                <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                  {tasksError}
                </Typography>
              )}

              {taskDiscoveryError && !taskDiscoveryDialogOpen && (
                <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                  {taskDiscoveryError}
                </Typography>
              )}

              <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
                <Box sx={{ flex: 1, border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1, p: 1, minHeight: 220 }}>
                  {tasksLoading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                      <CircularProgress size={20} />
                    </Box>
                  )}

                  {!tasksLoading && (!tasks || tasks.length === 0) && (
                    <Typography variant="caption" color="text.secondary">
                      No tasks yet. Create one manually or generate draft tasks from this change.
                    </Typography>
                  )}

                  {(tasks || []).map((task) => (
                    <Box
                      key={task.id}
                      onClick={() => setSelectedTaskId(task.id)}
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        cursor: "pointer",
                        border: "1px solid rgba(0,0,0,0.08)",
                        mb: 1,
                        backgroundColor: selectedTaskId === task.id ? "rgba(25,118,210,0.08)" : "transparent",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {task.task_order ? `${task.task_order}. ` : ""}{task.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {task.task_type || "GENERAL"} • {task.status || "DRAFT"}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ flex: 1.3, border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1, p: 1, minHeight: 220 }}>
                  {!selectedTask ? (
                    <Typography variant="caption" color="text.secondary">
                      Select a task to edit its scope, acceptance criteria, and status.
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>{selectedTaskSummary}</Typography>
                      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Task title"
                          value={selectedTask.title || ""}
                          onChange={(e) => setSelectedTask((prev) => ({ ...(prev || {}), title: e.target.value }))}
                        />
                        <TextField
                          size="small"
                          label="Order"
                          value={selectedTask.task_order ?? ""}
                          onChange={(e) => setSelectedTask((prev) => ({ ...(prev || {}), task_order: e.target.value }))}
                          sx={{ width: 120 }}
                        />
                      </Stack>

                      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        <TextField
                          size="small"
                          select
                          fullWidth
                          label="Task type"
                          value={selectedTask.task_type || "GENERAL"}
                          onChange={(e) => setSelectedTask((prev) => ({ ...(prev || {}), task_type: e.target.value }))}
                        >
                          {TASK_TYPES.map((option) => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          size="small"
                          select
                          fullWidth
                          label="Status"
                          value={selectedTask.status || "DRAFT"}
                          onChange={(e) => setSelectedTask((prev) => ({ ...(prev || {}), status: e.target.value }))}
                        >
                          {TASK_STATUSES.map((option) => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                          ))}
                        </TextField>
                      </Stack>

                      <TextField
                        size="small"
                        fullWidth
                        label="Description"
                        value={selectedTask.description || ""}
                        onChange={(e) => setSelectedTask((prev) => ({ ...(prev || {}), description: e.target.value }))}
                        multiline
                        minRows={3}
                        sx={{ mb: 1 }}
                      />

                      <TextField
                        size="small"
                        fullWidth
                        label="Scope summary"
                        value={selectedTask.scope_summary || ""}
                        onChange={(e) => setSelectedTask((prev) => ({ ...(prev || {}), scope_summary: e.target.value }))}
                        multiline
                        minRows={2}
                        sx={{ mb: 1 }}
                      />

                      <TextField
                        size="small"
                        fullWidth
                        label="Allowed files / scope boundaries"
                        value={selectedTask.allowed_files || ""}
                        onChange={(e) => setSelectedTask((prev) => ({ ...(prev || {}), allowed_files: e.target.value }))}
                        multiline
                        minRows={2}
                        helperText="Optional: list the intended files or folders this task is allowed to touch."
                        sx={{ mb: 1 }}
                      />

                      <TextField
                        size="small"
                        fullWidth
                        label="Acceptance criteria"
                        value={selectedTask.acceptance_criteria || ""}
                        onChange={(e) => setSelectedTask((prev) => ({ ...(prev || {}), acceptance_criteria: e.target.value }))}
                        multiline
                        minRows={3}
                        sx={{ mb: 1 }}
                      />


                      {String((selectedTask.task_type || "")).toUpperCase() === "SCHEMA" && (
                        <>
                          <TextField
                            size="small"
                            fullWidth
                            label="SQL proposal summary"
                            value={selectedTask.sql_summary || ""}
                            onChange={(e) => setSelectedTask((prev) => ({ ...(prev || {}), sql_summary: e.target.value }))}
                            multiline
                            minRows={2}
                            sx={{ mb: 1 }}
                          />
                          <TextField
                            size="small"
                            fullWidth
                            label="Proposed SQL"
                            value={selectedTask.sql_proposal || ""}
                            onChange={(e) => setSelectedTask((prev) => ({ ...(prev || {}), sql_proposal: e.target.value }))}
                            multiline
                            minRows={4}
                            sx={{ mb: 1 }}
                          />
                          <TextField
                            size="small"
                            fullWidth
                            label="Rollback SQL"
                            value={selectedTask.rollback_sql || ""}
                            onChange={(e) => setSelectedTask((prev) => ({ ...(prev || {}), rollback_sql: e.target.value }))}
                            multiline
                            minRows={3}
                            sx={{ mb: 1 }}
                          />
                        </>
                      )}
                      {String((selectedTask.task_type || "")).toUpperCase() === "SCHEMA" && taskSchemaTargetResult && (
                        <Box sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1, p: 1.5, mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>Schema target resolution</Typography>
                          <Typography variant="body2">Database: {taskSchemaTargetResult.schema_name || ""}</Typography>
                          <Typography variant="body2">Schema intent: {taskSchemaTargetResult.schema_intent || "UNRESOLVED"}</Typography>
                          <Typography variant="body2">Resolved table: {taskSchemaTargetResult.resolved_table || "(not resolved)"}</Typography>
                          <Typography variant="body2">Proposed new table: {taskSchemaTargetResult.proposed_table_name || ""}</Typography>
                          <Typography variant="body2">Target column: {taskSchemaTargetResult.column_name || ""}</Typography>
                          <Typography variant="body2">Confidence: {taskSchemaTargetResult.resolution_confidence || "UNRESOLVED"}</Typography>
                          {!!taskSchemaTargetResult.llm_confidence && (
                            <Typography variant="body2">LLM confidence: {taskSchemaTargetResult.llm_confidence}</Typography>
                          )}
                          {!!(taskSchemaTargetResult.matched_tables || []).length && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                              Top matches: {(taskSchemaTargetResult.matched_tables || []).slice(0, 5).map((row) => `${row.table_name} (${row.score})`).join(", ")}
                            </Typography>
                          )}
                          {!!taskSchemaTargetResult.llm_reason && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                              LLM reason: {taskSchemaTargetResult.llm_reason}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                            {taskSchemaTargetResult.guidance || ""}
                          </Typography>
                        </Box>
                      )}
                      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                        <Button variant="contained" onClick={handleSaveTask} disabled={taskSaveLoading}>
                          {taskSaveLoading ? "Saving..." : "Save Task"}
                        </Button>
                        <Button variant="outlined" onClick={handleRunTaskDiscovery} disabled={taskDiscoveryLoading || !selected?.project_id}>
                          {taskDiscoveryLoading ? "Scanning..." : "Run File Discovery"}
                        </Button>
                        {String((selectedTask.task_type || "")).toUpperCase() === "SCHEMA" && (
                          <>
                            <Button variant="outlined" onClick={handleResolveTaskSchemaTarget} disabled={taskSchemaTargetLoading || !canResolveTaskSchemaTarget}>
                              {taskSchemaTargetLoading ? "Resolving..." : "Resolve Schema Target"}
                            </Button>
                            <Button variant="outlined" onClick={handleRunTaskSqlProposal} disabled={taskSqlProposalLoading || !canRunTaskSqlProposal}>
                              {taskSqlProposalLoading ? "Generating SQL..." : "Generate SQL Proposal"}
                            </Button>
                            <Button variant="outlined" color="warning" onClick={handleOpenTaskSqlRunDialog} disabled={taskSqlRunLoading || !canRunSavedTaskSql}>
                              {taskSqlRunLoading && taskSqlRunDialogOpen ? "Loading SQL..." : "Run SQL Proposal"}
                            </Button>
                          </>
                        )}
                        <Button variant="outlined" onClick={handleRunTaskPatcher} disabled={taskPatchPreviewLoading || !canRunTaskPatcher}>
                          {taskPatchPreviewLoading ? "Building Patch..." : "Run Patcher"}
                        </Button>
                        <Button variant="outlined" color="error" onClick={handleDeleteTask} disabled={taskDeleteLoading}>
                          {taskDeleteLoading ? "Deleting..." : "Delete Task"}
                        </Button>
                      </Stack>
                      {!selected?.project_id && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                          Set a project on the Full Change before running file discovery or patcher.
                        </Typography>
                      )}
                      {selected?.project_id && String((selectedTask && selectedTask.task_type) || "").toUpperCase() === "SCHEMA" && taskSchemaTargetError && (
                        <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
                          {taskSchemaTargetError}
                        </Typography>
                      )}
                      {selected?.project_id && String((selectedTask && selectedTask.task_type) || "").toUpperCase() === "SCHEMA" && !taskSchemaTargetResult && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                          Resolve the schema target before generating SQL so the proposal is locked to the correct live table.
                        </Typography>
                      )}
                      {selected?.project_id && String((selectedTask && selectedTask.task_type) || "").toUpperCase() === "SCHEMA" && taskSchemaTargetResult && !canRunTaskSqlProposal && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                          SQL generation is blocked until the schema target resolves with strong confidence.
                        </Typography>
                      )}
                      {selected?.project_id && String((selectedTask && selectedTask.task_type) || "").toUpperCase() === "SCHEMA" && !canRunSavedTaskSql && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                          Save a SQL proposal on this task before running it.
                        </Typography>
                      )}
                      {selected?.project_id && !canRunTaskPatcher && (["FRONTEND", "BACKEND", "SCHEMA", "GENERAL"].includes(String((selectedTask && selectedTask.task_type) || "").toUpperCase())) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                          Approve at least one file into this task before running the patcher.
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Stack>

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle1">Create Planner Run (optional advisory lane)</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Planner remains optional. The primary workflow should start from the approved Full Change and its child tasks. Set the Full Change project and module first so downstream planner work inherits the correct scope.
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1, flexWrap: "wrap" }}>
                {selectedModuleOptions && selectedModuleOptions.length > 0 ? (
                  <TextField
                    size="small"
                    select
                    label="module"
                    value={plannerModule}
                    onChange={(e) => setPlannerModule(e.target.value)}
                    sx={{ minWidth: 220 }}
                  >
                    {selectedModuleOptions.map((row) => {
                      const moduleName = getModuleOptionValue(row);
                      return (
                        <MenuItem key={moduleName} value={moduleName}>
                          {getModuleOptionLabel(row)}
                        </MenuItem>
                      );
                    })}
                    {(plannerModule && !selectedModuleOptions.some((row) => getModuleOptionValue(row) === plannerModule)) ? (
                      <MenuItem value={plannerModule}>{getModuleOptionLabel(plannerModule)}</MenuItem>
                    ) : null}
                  </TextField>
                ) : (
                  <TextField size="small" label="module" value={plannerModule} onChange={(e) => setPlannerModule(e.target.value)} />
                )}
                <Button variant="outlined" onClick={handleCreatePlannerRun} disabled={plannerCreateLoading}>
                  {plannerCreateLoading ? "Working..." : "Create Planner Run"}
                </Button>
              </Stack>

              <TextField
                size="small"
                fullWidth
                label="planner goal text"
                value={plannerGoalText}
                onChange={(e) => setPlannerGoalText(e.target.value)}
                multiline
                minRows={4}
                sx={{ mb: 1 }}
              />

              {plannerCreateError && (
                <Typography color="error" variant="body2" sx={{ mb: 1 }}>{plannerCreateError}</Typography>
              )}

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle1">Linked Planner Runs</Typography>

              {plannerRunsError && (
                <Typography color="error" variant="body2" sx={{ mb: 1 }}>{plannerRunsError}</Typography>
              )}

              <Box sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1, p: 1 }}>
                {plannerRunsLoading && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={20} />
                  </Box>
                )}

                {!plannerRunsLoading && (!plannerRuns || plannerRuns.length === 0) && (
                  <Typography variant="caption" color="text.secondary">No planner runs linked to this Full Change.</Typography>
                )}

                {(plannerRuns || []).map((r) => {
                  const guardStatus = (r.status || "DRAFT").toUpperCase();
                  const notApproved = guardStatus !== "APPROVED";

                  return (
                    <Box key={r.id} sx={{ p: 1, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Run #{r.id} — {r.module || ""}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.status ? `Status: ${r.status}` : ""}
                        {r.planner_session_id !== null && r.planner_session_id !== undefined ? `${r.status ? " • " : ""}Session: ${r.planner_session_id}` : ""}
                        {r.created_at ? ` • Created: ${r.created_at}` : ""}
                      </Typography>

                      {notApproved && (
                        <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 0.5 }}>
                          Not approved — planner output is advisory only.
                        </Typography>
                      )}

                      {r.goal_text && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {String(r.goal_text).slice(0, 200)}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </>
          )}


          <Dialog open={taskSqlDialogOpen} onClose={() => { if (!taskSqlProposalLoading && !taskSqlApplyLoading) setTaskSqlDialogOpen(false); }} maxWidth="md" fullWidth>
            <DialogTitle>Schema SQL Proposal</DialogTitle>
            <DialogContent dividers>
              {taskSqlProposalError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {taskSqlProposalError}
                </Alert>
              )}
              {!taskSqlProposalResult && !taskSqlProposalLoading && (
                <Typography variant="body2" color="text.secondary">
                  No SQL proposal loaded.
                </Typography>
              )}
              {taskSqlProposalLoading && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Generating SQL proposal…</Typography>
                </Stack>
              )}
              {taskSqlProposalResult && (
                <>
                  {!!(taskSqlProposalResult?.schema_target || taskSchemaTargetResult) && (
                    <Box sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1, p: 1.5, mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Resolved schema target</Typography>
                      <Typography variant="body2">Database: {(taskSqlProposalResult?.schema_target || taskSchemaTargetResult)?.schema_name || ""}</Typography>
                      <Typography variant="body2">Schema intent: {(taskSqlProposalResult?.schema_target || taskSchemaTargetResult)?.schema_intent || ""}</Typography>
                      <Typography variant="body2">Table: {(taskSqlProposalResult?.schema_target || taskSchemaTargetResult)?.resolved_table || (taskSqlProposalResult?.schema_target || taskSchemaTargetResult)?.proposed_table_name || ""}</Typography>
                      <Typography variant="body2">Column: {(taskSqlProposalResult?.schema_target || taskSchemaTargetResult)?.column_name || ""}</Typography>
                      <Typography variant="body2">Confidence: {(taskSqlProposalResult?.schema_target || taskSchemaTargetResult)?.resolution_confidence || ""}</Typography>
                    </Box>
                  )}
                  <TextField
                    size="small"
                    fullWidth
                    label="Summary"
                    value={taskSqlSummaryDraft}
                    onChange={(e) => setTaskSqlSummaryDraft(e.target.value)}
                    multiline
                    minRows={3}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="Proposed SQL"
                    value={taskSqlProposalDraft}
                    onChange={(e) => setTaskSqlProposalDraft(e.target.value)}
                    multiline
                    minRows={8}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="Rollback SQL"
                    value={taskRollbackSqlDraft}
                    onChange={(e) => setTaskRollbackSqlDraft(e.target.value)}
                    multiline
                    minRows={6}
                    sx={{ mb: 2 }}
                  />
                  {!!(taskSqlProposalResult?.proposal?.resolved_files || []).length && (
                    <Typography variant="caption" color="text.secondary">
                      Inspected files: {(taskSqlProposalResult.proposal.resolved_files || []).join(", ")}
                    </Typography>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setTaskSqlDialogOpen(false)} disabled={taskSqlProposalLoading || taskSqlApplyLoading}>
                Close
              </Button>
              <Button onClick={handleRunTaskSqlProposal} variant="outlined" disabled={taskSqlProposalLoading || taskSqlApplyLoading || !canRunTaskSqlProposal}>
                Rebuild Proposal
              </Button>
              <Button onClick={handleApplyTaskSqlProposal} variant="contained" disabled={taskSqlProposalLoading || taskSqlApplyLoading || !String(taskSqlProposalDraft || "").trim()}>
                {taskSqlApplyLoading ? "Saving..." : "Accept SQL Proposal"}
              </Button>
            </DialogActions>
          </Dialog>

          {renderPlanningImportDialog()}
          {renderTargetPreviewDialog()}

          <Dialog open={taskSqlRunDialogOpen} onClose={() => { if (!taskSqlRunLoading) setTaskSqlRunDialogOpen(false); }} maxWidth="md" fullWidth>
            <DialogTitle>Run SQL Proposal</DialogTitle>
            <DialogContent dividers>
              {taskSqlRunError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {taskSqlRunError}
                </Alert>
              )}
              {taskSqlRunExecution && taskSqlRunExecution.success === true && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {taskSqlRunExecution.message || "SQL executed successfully."}
                </Alert>
              )}
              {taskSqlRunExecution && taskSqlRunExecution.success === false && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {taskSqlRunExecution.message || "SQL execution failed."}
                </Alert>
              )}
              {taskSqlRunLoading && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Preparing SQL execution details…</Typography>
                </Stack>
              )}
              {!taskSqlRunLoading && !taskSqlRunPreview && (
                <Typography variant="body2" color="text.secondary">
                  No SQL execution preview loaded.
                </Typography>
              )}
              {taskSqlRunPreview && (
                <Stack spacing={2}>
                  <Alert severity="warning">
                    Review the target database and exact SQL below before execution. This action runs against the configured project database.
                  </Alert>
                  <TextField
                    size="small"
                    fullWidth
                    label="Target database"
                    value={taskSqlRunPreview.database_name || ""}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="Schema source"
                    value={taskSqlRunPreview.schema_source || ""}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="SQL summary"
                    value={taskSqlRunPreview.sql_summary || ""}
                    InputProps={{ readOnly: true }}
                    multiline
                    minRows={2}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label={`SQL to run (${taskSqlRunPreview.statement_count || 0} statement${(taskSqlRunPreview.statement_count || 0) === 1 ? "" : "s"})`}
                    value={taskSqlRunPreview.sql_proposal || ""}
                    InputProps={{ readOnly: true }}
                    multiline
                    minRows={8}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="Rollback SQL"
                    value={taskSqlRunPreview.rollback_sql || ""}
                    InputProps={{ readOnly: true }}
                    multiline
                    minRows={5}
                  />
                  <Box sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1, p: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Execution outcome</Typography>
                    {!taskSqlRunExecution || taskSqlRunExecution.dry_run ? (
                      <Typography variant="body2" color="text.secondary">
                        Preview loaded. SQL has not been executed yet.
                      </Typography>
                    ) : (
                      <Stack spacing={1}>
                        <Typography variant="body2">
                          Database: {taskSqlRunExecution.database_name || taskSqlRunPreview.database_name || ""}
                        </Typography>
                        <Typography variant="body2">
                          Executed statements: {taskSqlRunExecution.executed_count || 0} / {taskSqlRunExecution.statement_count || 0}
                        </Typography>
                        {(taskSqlRunExecution.statement_results || []).map((result) => (
                          <Box key={result.index || JSON.stringify(result)} sx={{ border: "1px solid rgba(0,0,0,0.08)", borderRadius: 1, p: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Statement {result.index || "?"}
                            </Typography>
                            {result.preview && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                                {result.preview}
                              </Typography>
                            )}
                            {result.error ? (
                              <Typography variant="body2" color="error">
                                Error: {result.error}
                              </Typography>
                            ) : (
                              <>
                                <Typography variant="body2">Row count: {result.rowcount ?? 0}</Typography>
                                {result.returned_row_count !== undefined && (
                                  <Typography variant="body2">Returned rows: {result.returned_row_count}</Typography>
                                )}
                              </>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setTaskSqlRunDialogOpen(false)} disabled={taskSqlRunLoading}>
                Close
              </Button>
              <Button onClick={handleOpenTaskSqlRunDialog} variant="outlined" disabled={taskSqlRunLoading || !canRunSavedTaskSql}>
                Refresh Preview
              </Button>
              <Button onClick={handleExecuteTaskSqlProposal} variant="contained" color="warning" disabled={taskSqlRunLoading || !taskSqlRunPreview}>
                {taskSqlRunLoading && taskSqlRunPreview ? "Running SQL..." : "Run SQL Proposal"}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={taskPatchDialogOpen} onClose={() => { if (!taskPatchPreviewLoading && !taskPatchApplyLoading) setTaskPatchDialogOpen(false); }} maxWidth="lg" fullWidth>
            <DialogTitle>Task Patch Preview</DialogTitle>
            <DialogContent dividers>
              {taskPatchPreviewError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {taskPatchPreviewError}
                </Alert>
              )}
              {taskPatchApplyResult && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Applied {taskPatchApplyResult?.applied?.length || 0} file(s).
                </Alert>
              )}
              {!taskPatchPreviewResult && !taskPatchPreviewLoading && (
                <Typography variant="body2" color="text.secondary">
                  No patch preview yet.
                </Typography>
              )}
              {taskPatchPreviewLoading && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Building patch preview…</Typography>
                </Stack>
              )}
              {taskPatchPreviewResult && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {taskPatchPreviewResult.preview?.summary || "Review the diff(s) before apply."}
                  </Typography>
                  {(taskPatchPreviewResult.preview?.previews || []).map((p, idx) => (
                    <Box key={idx} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {p.path} {p.success ? "" : "(error)"}
                      </Typography>
                      {!p.success && (
                        <Alert severity="error" sx={{ mb: 1 }}>
                          {p.error || "Failed to build preview."}
                        </Alert>
                      )}
                      {p.success && (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            whiteSpace: "pre",
                            fontFamily: "monospace",
                            fontSize: 12,
                            overflow: "auto",
                            maxHeight: 320,
                          }}
                        >
                          {p.diff || "(no diff)"}
                        </Paper>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setTaskPatchDialogOpen(false)} disabled={taskPatchPreviewLoading || taskPatchApplyLoading}>
                Close
              </Button>
              <Button onClick={handleRunTaskPatcher} variant="outlined" disabled={taskPatchPreviewLoading || taskPatchApplyLoading}>
                Rebuild Preview
              </Button>
              <Button
                onClick={handleApplyTaskPatch}
                variant="contained"
                disabled={taskPatchPreviewLoading || taskPatchApplyLoading || !taskPatchPreviewResult}
              >
                {taskPatchApplyLoading ? "Applying..." : "Apply Patch"}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={taskDiscoveryDialogOpen} onClose={() => setTaskDiscoveryDialogOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>File Discovery Findings</DialogTitle>
            <DialogContent dividers>
              {!taskDiscoveryResult ? (
                <Typography variant="body2" color="text.secondary">No discovery findings loaded.</Typography>
              ) : (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {taskDiscoveryResult.findings?.summary || "Review the recommended files and accept the ones this change should use."}
                  </Typography>
                  {taskDiscoveryResult.findings?.recommended_primary_file && (
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                      Recommended primary file: {taskDiscoveryResult.findings.recommended_primary_file}
                    </Typography>
                  )}

                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Approve files</Typography>
                  <Box sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1, p: 1, mb: 2, maxHeight: 260, overflowY: "auto" }}>
                    {(taskDiscoveryResult.findings?.candidates || []).map((row) => (
                      <Box key={row.path} sx={{ borderBottom: "1px solid rgba(0,0,0,0.08)", py: 1 }}>
                        <FormControlLabel
                          control={<Checkbox checked={taskDiscoverySelectedFiles.includes(row.path)} onChange={() => toggleDiscoveryFile(row.path)} />}
                          label={row.path}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", pl: 4 }}>
                          {row.reason || row.description || "Candidate file"}
                        </Typography>
                      </Box>
                    ))}
                    {(!taskDiscoveryResult.findings?.candidates || taskDiscoveryResult.findings.candidates.length === 0) && (
                      <Typography variant="caption" color="text.secondary">No candidate files found.</Typography>
                    )}
                  </Box>

                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Apply findings to tasks</Typography>
                  <Box sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1, p: 1 }}>
                    {(taskDiscoveryResult.findings?.target_task_suggestions || []).map((row) => (
                      <FormControlLabel
                        key={row.id}
                        control={<Checkbox checked={taskDiscoveryTargetIds.includes(row.id)} onChange={() => toggleDiscoveryTargetTask(row.id)} />}
                        label={`#${row.task_order || row.id} ${row.title} (${row.task_type || "GENERAL"})`}
                      />
                    ))}
                    {(!taskDiscoveryResult.findings?.target_task_suggestions || taskDiscoveryResult.findings.target_task_suggestions.length === 0) && (
                      <Typography variant="caption" color="text.secondary">No downstream task suggestions were found. The findings will still be written back to the current task.</Typography>
                    )}
                  </Box>

                  {taskDiscoveryError && (
                    <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                      {taskDiscoveryError}
                    </Typography>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setTaskDiscoveryDialogOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleApplyTaskDiscovery} disabled={taskDiscoveryApplyLoading || taskDiscoverySelectedFiles.length === 0}>
                {taskDiscoveryApplyLoading ? "Applying..." : "Accept Findings"}
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Stack>
    </Box>
  );
};

export default FullChangePanel;
